"use client";
/* eslint-disable @typescript-eslint/no-explicit-any */

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from "react";
import { useRouter } from "next/navigation";
import { pageVariants, headerVariants, slideFromBottomVariants, cardVariants, dialogVariants, mountSafeRevealVariants } from "@/lib/animations";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import HotelsTable, { type HotelsTableRow } from '@/components/ui/HotelsTable';
import ProductDetailMenu, { type MenuItems } from "@/components/ui/ProductDetailMenu";
import Faqs from "@/components/sections/Faqs";
import TourRouteMap, { type TourRouteMapRef } from "@/components/ui/TourRouteMap";
import TourRouteMapLegend from "@/components/ui/TourRouteMapLegend";
import PriceDetailTable from "@/components/ui/PriceDetailTable";
import ScrollProgressIndicator from "@/components/ui/ScrollProgressIndicator";
import InteractiveMapStory from "@/components/ui/InteractiveMapStory";
import ProductTourInfoCard from "@/components/ui/ProductTourInfoCard";
import ProductTourServicesList from "@/components/ui/ProductTourServicesList";
import DepartureDatesCalendar from "@/components/ui/DepartureDatesCalendar";
import ProductReviewsList from "@/components/ui/ProductReviewsList";
import ProductReviewForm from "@/components/ui/ProductReviewForm";
import ProductTourHero from "./sections/ProductTourHero";
import ProductTourDescription from "./sections/ProductTourDescription";
import ProductShareButton from "@/components/ui/ProductShareButton";
import BrochureDownloadModal from "@/components/ui/BrochureDownloadModal";
import type { PageUrlMap } from "@/types/blog";
import ReservationBookingSidebar, { type TourPricingPeriod } from "@/components/ui/ReservationBookingSidebar";
import ReservationBookingSidebarSkeleton from "@/components/ui/ReservationBookingSidebarSkeleton";
import OverviewAccordion, { type OverviewAccordionItem } from "@/components/ui/OverviewAccordion";
import RequestForm from "@/components/ui/RequestForm";
import StepperIconButton from "@/components/ui/StepperIconButton";
import TourDepartureMonthPicker, { type SelectedMonthFallback } from "@/components/ui/TourDepartureMonthPicker";
import { useProductTourData } from "@/hooks/useProductTourData";
import { useProductReviews, Review } from "@/hooks/useProductReviews";
import { useProductBreadcrumbs } from "@/hooks/useProductBreadcrumbs";
import MediaImage from "@/components/ui/MediaImage";
import HeritageSiteEmbedMap from "@/components/ui/HeritageSiteEmbedMap";
import { decodeHtmlEntitiesPlain } from "@/lib/decodeHtmlEntitiesPlain";
import { Fancybox } from "@fancyapps/ui/dist/fancybox/";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { transformMediaUrl } from "@/lib/media-url-transformer";
import { parseSpecialGuestsFromProduct } from "@/lib/special-guests";
import { resolveGroupDeparturePrice } from "@/lib/tour-departure-pricing";
import { stripTourProgramTypography } from "@/lib/strip-tour-program-html";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";
import { clientFetchJsonDedupe } from "@/lib/client-fetch-dedupe";
import { isOtherDescriptionsGroup, resolveRepeaterSectionTitle } from "@/utils/productOtherDescriptionsUtils";
import { buildDayFirstMapIndex, buildTourMapLocations, firstNonEmpty } from "@/utils/buildTourMapLocations";
import { extractOrderedHotelEntries, extractOrderedHotelIds, sortHotelsByIdOrder } from '@/utils/extractOrderedHotelIds';
import { resolveUrlFromMap } from "@/lib/blog-transformers";
import { usePageUrls } from "@/contexts/PageUrlsContext";
import { resolveReservationBookNowUrl } from "@/lib/url-helpers";
import { resolveSidebarFormHeading } from "@/utils/tour-sidebar-form-mode";
import type { FaqEntry } from "@/types/faq";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import CustomerApiService from "@/services/CustomerApiService";
import Link from "next/link";
import { RESERVATION_CHECKOUT_COOKIE_NAME, RESERVATION_PRICING_STORAGE_KEY } from "@/lib/cookies";

/** Öğün metni: `meal` + isteğe bağlı `meals_custom` (API ayrı alanlar) */
function formatTourDayMealDisplay(meal: unknown, mealsCustom?: string | null): string {
    let main = "";
    if (meal != null && meal !== "") {
        if (Array.isArray(meal)) {
            main = meal.map((m) => String(m).trim()).filter(Boolean).join(", ");
        } else {
            main = String(meal).trim();
        }
    }
    const extra = mealsCustom?.trim() ?? "";
    if (!main && !extra) return "";
    if (!extra) return main;
    if (!main) return extra;
    return `${main} — ${extra}`;
}

/** API sayı veya metin (örn. "320 km", "4 saat") dönebilir */
function formatTourDayDistanceDisplay(distance: unknown): string {
    if (distance == null || distance === "") return "";
    if (typeof distance === "number" && !Number.isNaN(distance)) {
        return `${distance} km`;
    }
    return String(distance).trim();
}

type HeritageForeignTicket = { ticket_label?: string; price?: number | string; currency_code?: string };

/** Kart önizlemesi: HTML’den düz metin, kısaltılmış (çalışma saatleri burada gösterilmez). */
function heritageDescriptionCardExcerpt(description?: string | null, maxLen = 200): string {
    if (!heritageDescriptionIsOpenable(description)) return "";
    const stripped = description!
        .trim()
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ");
    const textOnly = decodeHtmlEntitiesPlain(stripped).replace(/\s+/g, " ").trim();
    if (textOnly.length <= maxLen) return textOnly;
    return `${textOnly.slice(0, maxLen - 1).trimEnd()}…`;
}

function heritageSiteRowHasValidCoords(row: HeritageSitesExpandedRow): boolean {
    const lat = row.latitude;
    const lng = row.longitude;
    return (
        lat != null &&
        lng != null &&
        Number.isFinite(Number(lat)) &&
        Number.isFinite(Number(lng)) &&
        Math.abs(Number(lat)) <= 90 &&
        Math.abs(Number(lng)) <= 180
    );
}

/** Modalda gösterilecek en az bir blok varsa karttan detay açılabilir. */
function heritageSiteRowHasModalContent(row: HeritageSitesExpandedRow): boolean {
    if (heritageDescriptionIsOpenable(row.description)) return true;
    if (row.location?.trim()) return true;
    if (heritageSiteRowHasValidCoords(row)) return true;
    if (row.working_hours?.trim()) return true;
    if (heritageModalTicketLines(row).length > 0) return true;
    if (row.cover_image_url?.trim()) return true;
    const gal = row.gallery_image_urls;
    return Array.isArray(gal) && gal.some((u) => typeof u === "string" && u.trim().length > 0);
}

function currencyCodeToSymbol(code: string): string {
    const c = code.trim().toUpperCase();
    if (c === "EUR") return "€";
    if (c === "TRY" || c === "TRL") return "₺";
    if (c === "USD") return "$";
    if (c === "GBP") return "£";
    return c;
}

/** Metindeki para birimi kodlarını (EUR vb.) simgeye çevirir. */
function heritageReplaceCurrencyCodesWithSymbols(text: string): string {
    if (!text) return text;
    return text
        .replace(/\bEUR\b/gi, "€")
        .replace(/\bTRY\b/gi, "₺")
        .replace(/\bTRL\b/gi, "₺")
        .replace(/\bUSD\b/gi, "$")
        .replace(/\bGBP\b/gi, "£");
}

/**
 * Bilet özeti: mümkünse sadece fiyat + simge; uzun DÖSİM etiketi title/tooltip için ayrılır.
 */
function heritageTicketPriceCompact(row: HeritageSitesExpandedRow): { display: string; title?: string } {
    const tickets = row.foreign_visitor_tickets;
    if (Array.isArray(tickets) && tickets.length > 0) {
        const t0 = tickets[0] as HeritageForeignTicket;
        const price = t0?.price;
        const code = (t0?.currency_code || "EUR").toString().toUpperCase();
        const label = typeof t0?.ticket_label === "string" && t0.ticket_label.trim() ? t0.ticket_label.trim() : undefined;
        if (price !== null && price !== undefined && String(price).trim() !== "") {
            const display = `${String(price).trim()} ${currencyCodeToSymbol(code)}`;
            return { display, title: label };
        }
    }
    const ap = row.admission_price?.trim();
    if (!ap) return { display: "" };
    const idx = ap.indexOf(":");
    if (idx > 0 && idx < ap.length - 1) {
        const rest = ap.slice(idx + 1).trim();
        const head = ap.slice(0, idx).trim();
        if (/[\d.,]+\s*[A-Za-z]{2,4}\b/.test(rest) || /^\d/.test(rest)) {
            return { display: heritageReplaceCurrencyCodesWithSymbols(rest), title: head };
        }
    }
    return { display: heritageReplaceCurrencyCodesWithSymbols(ap) };
}

/** Açıklama HTML/metin olarak anlamlı içerik varsa (modal için). */
function heritageDescriptionIsOpenable(description?: string | null): boolean {
    if (description == null || typeof description !== "string") return false;
    const trimmed = description.trim();
    if (!trimmed) return false;
    const textOnly = trimmed.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();
    return textOnly.length > 0;
}

const HERITAGE_DOSIM_TICKET_LABEL_TR = "Yabancı ziyaretçi (DÖSİM)";

function heritageLocalizedTicketLabelForDisplay(
    label: string,
    language: string | undefined,
    t: (key: string, fallback?: string) => string,
): string {
    const trimmed = label.trim();
    if (!trimmed || language === "tr") return label;
    if (trimmed === HERITAGE_DOSIM_TICKET_LABEL_TR) {
        return t("tour_detail.heritage_ticket_foreign_visitor_dosim", "Foreign visitor (DÖSİM)");
    }
    return label;
}

/** Eski API / önbellek: çalışma saati metnindeki Türkçe gün adlarını hedef dilde gösterir. */
function heritageLocalizeWorkingHoursText(text: string, language: string | undefined): string {
    if (!text.trim() || language === "tr") return text;
    const pairs: [string, string][] = [
        ["Pazartesi", "Monday"],
        ["Çarşamba", "Wednesday"],
        ["Cumartesi", "Saturday"],
        ["Perşembe", "Thursday"],
        ["Salı", "Tuesday"],
        ["Cuma", "Friday"],
        ["Pazar", "Sunday"],
    ];
    let out = text;
    for (const [tr, en] of pairs) {
        out = out.split(tr).join(en);
    }
    return out.split("Kapalı").join("Closed");
}

function heritageLocalizedAdmissionSummaryLine(
    line: string,
    language: string | undefined,
    t: (key: string, fallback?: string) => string,
): string {
    if (!line.trim() || language === "tr") return line;
    if (line.includes(HERITAGE_DOSIM_TICKET_LABEL_TR)) {
        const en = t("tour_detail.heritage_ticket_foreign_visitor_dosim", "Foreign visitor (DÖSİM)");
        return line.split(HERITAGE_DOSIM_TICKET_LABEL_TR).join(en);
    }
    return line;
}

/** Modal bilet listesi satırları; `language` + `t` verilirse bilinen Türkçe etiketler çeviri tablosundan gelir. */
function heritageModalTicketLines(
    row: HeritageSitesExpandedRow,
    language?: string,
    t?: (key: string, fallback?: string) => string,
): string[] {
    const out: string[] = [];
    const tickets = row.foreign_visitor_tickets;
    if (Array.isArray(tickets)) {
        for (const tk of tickets) {
            if (!tk || typeof tk !== "object") continue;
            const tkv = tk as HeritageForeignTicket;
            const labelRaw = typeof tkv.ticket_label === "string" && tkv.ticket_label.trim() ? tkv.ticket_label.trim() : "";
            const label =
                language && t ? heritageLocalizedTicketLabelForDisplay(labelRaw, language, t) : labelRaw;
            const price = tkv.price;
            if (price === null || price === undefined || String(price).trim() === "") continue;
            const code = (tkv.currency_code || "EUR").toString();
            const symPart = `${String(price).trim()} ${currencyCodeToSymbol(code)}`;
            out.push(label ? `${label}: ${symPart}` : symPart);
        }
    }
    if (out.length === 0 && row.admission_price?.trim()) {
        const raw = heritageReplaceCurrencyCodesWithSymbols(row.admission_price.trim());
        out.push(language && t ? heritageLocalizedAdmissionSummaryLine(raw, language, t) : raw);
    }
    return out;
}

type HeritageSitesExpandedRow = {
    id: number;
    name: string;
    /** Ürün açıklaması (dil kaydına göre API); kartta özet + modalda HTML */
    description?: string | null;
    /** Ören yeri meta: enlem (harita embed, modalda yüklenir) */
    latitude?: number | null;
    /** Ören yeri meta: boylam */
    longitude?: number | null;
    location?: string;
    /** API: bilet satırlarından üretilmiş tek satır özet veya eski metin */
    admission_price?: string;
    working_hours?: string;
    foreign_visitor_tickets?: HeritageForeignTicket[];
    ticket_prices_source_url?: string;
    cover_image_url?: string | null;
    /** Galeri görselleri (proxy URL), medya modalı ile uyumlu sıra */
    gallery_image_urls?: string[];
};

interface ProductTourDetailProps {
  product?: {
    id?: number;
    slug?: string;
    name?: string;
    type?: string;
    product_type?: string;
    description?: string;
    gallery?: Array<any>;
    metas?: Record<string, any>;
    fields?: Record<string, any>;
    tour_pricing?: Array<any>;
    /** Tur detay embed: her turda görünen sabit içerikler (tour_static; overview akordiyonu) */
    tour_static?: Array<{ id: number; name: string; slug: string; description: string }>;
    currency?: {
      code?: string;
    };
    categories?: Array<{
      id?: number;
      name?: string;
      slug?: string;
    }>;
    has_price?: boolean;
    booking_flow?: 'request_first' | 'instant_online' | 'bank_transfer_only';
    booking_flow_configured?: boolean;
  };
  language?: string;
  languageId?: number;
  translations?: Record<string, string>;
  menuItems?: MenuItems[];
  productFaqs?: FaqEntry[];
  reviewForm?: {
    id?: number;
    slug?: string;
    title?: string;
  } | null;
  reviews?: Review[];
  averageRating?: number;
  reviewCount?: number;
  hasMoreReviews?: boolean;
  pageUrls?: PageUrlMap;
  contractsPageUrl?: string;
  defaultLanguage?: string;
  initialFormDetails?: any;
  /** Sayfa render sırasında çekilmiş talep formu detayı (tek API ile cache) */
  initialRequestFormDetails?: any;
  /** Sunucuda forms/details?for_reservation=1 tam yanıt (sözleşmeler + form meta) */
  initialReservationFormDetails?: { id?: number; title?: string; agreements?: { required?: Array<{ id: number; title: string; slug: string; content?: string; is_required?: boolean }>; optional?: Array<{ id: number; title: string; slug: string; content?: string; is_required?: boolean }> } } | null;
  /** Sunucuda forms/details'ten gelen rezervasyon sözleşmeleri; varsa client-side forms/details çağrısı yapılmaz */
  initialReservationFormAgreements?: Array<{ id: number; title: string; slug: string; is_required: boolean; content?: string }>;
  /** Sayfa verisinde embed ile gelen otel listesi (Oteller sekmesi); varsa client-side /api/products çağrısı yapılmaz */
  initialTourHotels?: Array<{ id: number; name: string; slug: string }>;
  /** Sayfa yüklenirken product endpoint'inden gelen çıkış tarihleri (embed=tour_detail); discount_percentage ile kampanya dahil */
  initialTourDepartureDates?: Array<{ id: number; period_id?: number; departure_date: string; quota: number | null; status: string; discount_percentage?: number | null }>;
}


export default function ProductTourDetail({ 
  product, 
  language = 'en',
  languageId,
  translations = {},
  menuItems,
  productFaqs = [],
  reviewForm,
  reviews: initialReviews = [],
  averageRating: initialAverageRating = 0,
  reviewCount: initialReviewCount = 0,
  hasMoreReviews: initialHasMoreReviews = false,
  pageUrls,
  contractsPageUrl,
  defaultLanguage,
  initialFormDetails,
  initialRequestFormDetails,
  initialReservationFormDetails = null,
  initialReservationFormAgreements = [],
  initialTourHotels,
  initialTourDepartureDates,
}: ProductTourDetailProps = {}) {
    const router = useRouter();
    const layoutPageUrls = usePageUrls();
    const effectivePageUrls = layoutPageUrls ?? pageUrls;
    const effectiveDefaultLanguage = defaultLanguage ?? "en";
    const bookNowHref = useMemo(
        () => resolveReservationBookNowUrl(effectivePageUrls, language, effectiveDefaultLanguage),
        [effectivePageUrls, language, effectiveDefaultLanguage],
    );
    const RESERVATION_COOKIE_MAX_AGE_DAYS = 7;
    const [openTourDay, setOpenTourDay] = useState<string | null>(null);
    const [allExpanded, setAllExpanded] = useState(false);
    const [resolvedProductFaqs, setResolvedProductFaqs] = useState<FaqEntry[]>(productFaqs);
    const [brochureModalOpen, setBrochureModalOpen] = useState(false);
    const [isStoryModeActive, setIsStoryModeActive] = useState(false);
    const [mapActiveIndex, setMapActiveIndex] = useState<number | null>(null);
    const [hotels, setHotels] = useState<any[]>(initialTourHotels ?? []);
    const [hotelsLoading, setHotelsLoading] = useState(false);
    const [hotelsFetchTriggered, setHotelsFetchTriggered] = useState(false);
    const tourHotelsMeta = product?.metas?.tour_hotels;
    /** Admin’deki Oteller sekmesi notu (rich_text); API ProductController zaten sanitize + decode eder */
    const tourHotelsNoteHtml = useMemo(() => {
        const raw = product?.metas?.tour_hotels_note;
        return typeof raw === 'string' && raw.trim() ? raw : '';
    }, [product?.metas?.tour_hotels_note]);

    const heritageSitesExpanded = useMemo((): HeritageSitesExpandedRow[] => {
        const raw = (product as { metas?: { heritage_sites_expanded?: unknown } })?.metas?.heritage_sites_expanded;
        if (!Array.isArray(raw)) return [];
        return raw.filter(
            (row): row is HeritageSitesExpandedRow =>
                row != null && typeof row === "object" && typeof (row as { id?: unknown }).id === "number"
        ) as HeritageSitesExpandedRow[];
    }, [product]);

    const showHeritageSitesSection = useMemo(() => heritageSitesExpanded.length > 0, [heritageSitesExpanded.length]);

    const specialGuestsFromProduct = useMemo(
        () => parseSpecialGuestsFromProduct(product as { metas?: Record<string, unknown> }, transformMediaUrl),
        [product],
    );
    const [specialGuestsClient, setSpecialGuestsClient] = useState<typeof specialGuestsFromProduct | null>(null);

    useEffect(() => {
        setSpecialGuestsClient(null);
    }, [(product as { id?: number })?.id]);

    useEffect(() => {
        if (specialGuestsFromProduct.length > 0) {
            setSpecialGuestsClient(null);
            return;
        }
        const slug = (product as { slug?: string })?.slug;
        if (!slug) return;
        let cancelled = false;
        fetch(
            `/api/products/${encodeURIComponent(slug)}?language=${encodeURIComponent(language ?? "en")}&type=tour&embed=tour_detail&view=page`,
        )
            .then((res) => res.json())
            .then((payload: { data?: { metas?: Record<string, unknown> } }) => {
                if (cancelled || !payload?.data) return;
                const parsed = parseSpecialGuestsFromProduct(payload.data, transformMediaUrl);
                setSpecialGuestsClient(parsed.length > 0 ? parsed : null);
            })
            .catch(() => {
                if (!cancelled) setSpecialGuestsClient(null);
            });
        return () => {
            cancelled = true;
        };
    }, [specialGuestsFromProduct.length, product, language, transformMediaUrl]);

    const specialGuests =
        specialGuestsFromProduct.length > 0
            ? specialGuestsFromProduct
            : (specialGuestsClient ?? []);
    const showSpecialGuestsSection = specialGuests.length > 0;
    const specialGuestsGridClass = useMemo(() => {
        const n = specialGuests.length;
        if (n <= 1) return "lg:grid-cols-1 lg:max-w-xs";
        if (n === 2) return "lg:grid-cols-2 lg:max-w-2xl";
        if (n === 3) return "lg:grid-cols-3 lg:max-w-4xl";
        if (n === 4) return "lg:grid-cols-4 lg:max-w-5xl";
        return "lg:grid-cols-5";
    }, [specialGuests.length]);

    const [isFavorite, setIsFavorite] = useState(false);
    const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
    const [hasMoreReviews, setHasMoreReviews] = useState(initialHasMoreReviews);
    const [currentPage, setCurrentPage] = useState(1);
    const [appendedReviews, setAppendedReviews] = useState<Review[]>([]);
    const [reservationFormAgreements, setReservationFormAgreements] = useState<Array<{ id: number; title: string; slug: string; is_required: boolean; content?: string }>>(() => {
        if (initialReservationFormAgreements.length > 0) return initialReservationFormAgreements;
        const ag = initialReservationFormDetails?.agreements;
        if (!ag) return [];
        const required = (ag.required || []).map((a) => ({
            id: a.id,
            title: a.title,
            slug: a.slug,
            is_required: true as const,
            content: a.content,
        }));
        const optional = (ag.optional || []).map((a) => ({
            id: a.id,
            title: a.title,
            slug: a.slug,
            is_required: false as const,
            content: a.content,
        }));
        return [...required, ...optional];
    });
    const [reservationAgreementsLoading, setReservationAgreementsLoading] = useState(() => {
        if (initialReservationFormAgreements.length > 0) return false;
        const ag = initialReservationFormDetails?.agreements;
        const hasAgreements = (ag?.required?.length ?? 0) > 0 || (ag?.optional?.length ?? 0) > 0;
        return !hasAgreements;
    });
    const requestFormTitleFromApi = (initialRequestFormDetails as { title?: string } | null | undefined)?.title ?? null;
    const effectiveReviewForm = reviewForm ?? { slug: 'product-review-form', title: 'Product Review Form' };
    const [reservationModalOpen, setReservationModalOpen] = useState(false);
    const [requestFormModalOpen, setRequestFormModalOpen] = useState(false);
    const [heritageModalRow, setHeritageModalRow] = useState<HeritageSitesExpandedRow | null>(null);

    useEffect(() => {
        if (!heritageModalRow) return;
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") setHeritageModalRow(null);
        };
        window.addEventListener("keydown", onEsc);
        return () => window.removeEventListener("keydown", onEsc);
    }, [heritageModalRow]);

    useEffect(() => {
        if (!requestFormModalOpen) return;
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setRequestFormModalOpen(false);
        };
        document.addEventListener('keydown', handleEscape);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = prevOverflow;
        };
    }, [requestFormModalOpen]);

    const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
    const [checkAvailabilityMonth, setCheckAvailabilityMonth] = useState(() => currentMonth);
    const [checkAvailabilityAdults, setCheckAvailabilityAdults] = useState(2);
    const [checkAvailabilityChildren, setCheckAvailabilityChildren] = useState(0);
    const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
    const [isDatesSectionMonthPickerOpen, setIsDatesSectionMonthPickerOpen] = useState(false);
    const [monthPickerYear, setMonthPickerYear] = useState(() => new Date().getFullYear());
    const [sidebarBookingTab, setSidebarBookingTab] = useState<'group' | 'private'>('group');
    type DepartureDateItem = {
        id: number;
        period_id?: number;
        departure_date: string;
        quota: number | null;
        status: string;
        discount_percentage: number | null;
        is_bookable?: boolean;
        availability?: 'available' | 'limited' | 'sold_out' | 'stop_sale';
        remaining_pax?: number | null;
    };
    const [groupDepartureDatesFromClient, setGroupDepartureDatesFromClient] = useState<DepartureDateItem[]>([]);
    const departureListEnrichedRef = useRef(false);
    const [departureMetaPatch, setDepartureMetaPatch] = useState<Record<number, Partial<DepartureDateItem>>>({});

    /** Çıkış tarihleri: sunucudan gelen varsa onu kullan, yoksa client'ta tek seferlik çekilen (indirim dahil, ilk yüklemede fiyat/ay dropdown için) */
    const groupDepartureDates: DepartureDateItem[] = useMemo(() => {
        const fromServer = Array.isArray(initialTourDepartureDates) && initialTourDepartureDates.length > 0
            ? (initialTourDepartureDates as DepartureDateItem[])
            : [];
        const base = fromServer.length > 0 ? fromServer : groupDepartureDatesFromClient;
        return base.map((d) => ({ ...d, ...(departureMetaPatch[d.id] ?? {}) }));
    }, [initialTourDepartureDates, groupDepartureDatesFromClient, departureMetaPatch]);
    const monthPickerRef = useRef<HTMLDivElement | null>(null);
    const datesSectionMonthPickerRef = useRef<HTMLDivElement | null>(null);
    const [groupDepartureDatesLoading, setGroupDepartureDatesLoading] = useState(false);
    /** Seçili ay + pax için kampanya ve çıkış indirimi dahil fiyatlar (tek API kaynağı) */
    const [groupDeparturePrices, setGroupDeparturePrices] = useState<Array<{
        id?: number;
        departure_date: string;
        discount_percentage?: number | null;
        calculated_price: {
            success: boolean;
            price_per_person?: number | null;
            original_price_per_person?: number | null;
            final_price?: number;
            discount_amount?: number;
            applied_campaigns?: Array<{ id: number; name: string; discount_amount?: number; discount_percentage?: number }>;
        };
    }>>([]);
    const [groupDeparturePricesLoading, setGroupDeparturePricesLoading] = useState(false);
    const mapRef = useRef<TourRouteMapRef>(null);
    const mapSectionRef = useRef<HTMLDivElement>(null);
    /** Tur programı gün görselleri — Fancybox galerisi (gün başına ayrı grup) */
    const itineraryFancyboxRef = useRef<HTMLDivElement | null>(null);
    const { isAuthenticated, customer } = useCustomerAuth();

    const [lazyPricing, setLazyPricing] = useState<{
        tour_pricing?: Array<Record<string, unknown>>;
        has_price?: boolean;
    } | null>(null);
    const [pricingLoading, setPricingLoading] = useState(false);
    const [pricingLoadFailed, setPricingLoadFailed] = useState(false);

    const needsLazyPricing = useMemo(() => {
        const pType = product?.type ?? product?.product_type;
        if (pType !== "tour" && pType !== "ship_tour") {
            return false;
        }
        return !(Array.isArray(product?.tour_pricing) && product.tour_pricing.length > 0);
    }, [product?.type, product?.product_type, product?.tour_pricing]);

    const pricingPending = needsLazyPricing && pricingLoading;

    const tourPricingSource = useMemo(
        () => lazyPricing?.tour_pricing ?? product?.tour_pricing ?? [],
        [lazyPricing?.tour_pricing, product?.tour_pricing],
    );

    const effectiveHasPrice = lazyPricing?.has_price ?? product?.has_price;

    useEffect(() => {
        if (!needsLazyPricing) {
            setPricingLoading(false);
            return;
        }
        const slug = product?.slug;
        const pType = product?.type ?? product?.product_type;
        if (!slug || !pType) {
            return;
        }
        let cancelled = false;
        setLazyPricing(null);
        setPricingLoadFailed(false);
        setPricingLoading(true);
        const qs = new URLSearchParams({
            language: language ?? "en",
            type: String(pType),
        });
        fetch(`/api/products/${encodeURIComponent(slug)}/pricing?${qs.toString()}`, { credentials: "same-origin" })
            .then((res) => (res.ok ? res.json() : null))
            .then((json: { data?: { tour_pricing?: Array<Record<string, unknown>>; has_price?: boolean } } | null) => {
                if (cancelled) {
                    return;
                }
                if (!json?.data) {
                    setLazyPricing(null);
                    setPricingLoadFailed(true);
                    return;
                }
                const periods = json.data.tour_pricing ?? [];
                setLazyPricing({
                    tour_pricing: periods,
                    has_price: json.data.has_price,
                });
                setPricingLoadFailed(periods.length === 0 && (json.data.has_price ?? product?.has_price ?? false));
            })
            .catch(() => {
                if (!cancelled) {
                    setLazyPricing(null);
                    setPricingLoadFailed(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setPricingLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [needsLazyPricing, product?.slug, product?.type, product?.product_type, product?.has_price, language]);

    const productForTourData = useMemo(() => {
        if (!product) {
            return product;
        }
        const tourPricing = tourPricingSource;
        if (Array.isArray(tourPricing) && tourPricing.length > 0) {
            return { ...product, tour_pricing: tourPricing };
        }
        return product;
    }, [product, tourPricingSource]);

    const hasPrivatePrice = useMemo(() => tourPricingSource.some((p: { periodType?: string }) => p.periodType === 'private'), [tourPricingSource]);
    const hasDailyTourPrice = useMemo(
        () => tourPricingSource.some((p: { periodType?: string }) => p.periodType === 'daily'),
        [tourPricingSource],
    );
    /** Rezervasyon modalı: private veya günlük tur fiyatı */
    const hasBookablePrivateOrDailyPrice = useMemo(
        () => hasPrivatePrice || hasDailyTourPrice,
        [hasPrivatePrice, hasDailyTourPrice],
    );
    const reservationPricingPeriodType = useMemo((): 'private' | 'daily' => {
        if (hasDailyTourPrice) return 'daily';
        const m = (product as { metas?: Record<string, unknown> })?.metas;
        const tt = String(m?.tour_type ?? m?.field_tour_type ?? '').toLowerCase();
        if (tt === 'daily-tour' || tt === 'daily') return 'daily';
        return 'private';
    }, [hasDailyTourPrice, product]);
    const hasGroupPrice = useMemo(() => tourPricingSource.some((p: { periodType?: string }) => p.periodType === 'group'), [tourPricingSource]);
    /** Grup çıkışı + private/günlük fiyat aynı turda */
    const hasDualGroupAndPrivateSidebar = useMemo(
        () =>
            hasGroupPrice &&
            hasBookablePrivateOrDailyPrice &&
            groupDepartureDates.length > 0,
        [hasGroupPrice, hasBookablePrivateOrDailyPrice, groupDepartureDates.length],
    );
    const showSidebarGroupBooking = hasGroupPrice && groupDepartureDates.length > 0;
    const showSidebarPrivateBooking =
        product?.id != null && (pricingPending || hasBookablePrivateOrDailyPrice);
    /** Rezervasyon yoksa sağ barda gömülü talep formu */
    const showInlineRequestForm =
        product?.id != null &&
        (groupDepartureDates.length === 0 || !hasGroupPrice) &&
        !hasBookablePrivateOrDailyPrice &&
        !pricingPending;
    /** Favoriler kartında Tour Request Form butonu — private/group tüm rezervasyonlu turlarda */
    const showRequestFormSidebarButton = product?.id != null && !showInlineRequestForm;
    const sidebarUseBookingTabs =
        hasDualGroupAndPrivateSidebar && showSidebarGroupBooking && showSidebarPrivateBooking;

    useEffect(() => {
        if (sidebarUseBookingTabs) {
            setSidebarBookingTab('group');
        }
    }, [sidebarUseBookingTabs, product?.id]);

    // Grup fiyatlarından kişi başı minimum fiyat (garantili çıkış "X €'dan" göstermek için)
    const groupMinPricePerPerson: number | null = useMemo(() => {
        const groupPeriods = tourPricingSource.filter((p: { periodType?: string; rows?: Array<{ perPersonDouble?: number | null }> }) => p.periodType === 'group' && p.rows?.length);
        let min: number | null = null;
        groupPeriods.forEach((p: { rows?: Array<{ perPersonDouble?: number | null }> }) => {
            p.rows?.forEach((r: { perPersonDouble?: number | null }) => {
                const v = r.perPersonDouble != null ? Number(r.perPersonDouble) : null;
                if (v != null && !Number.isNaN(v) && (min == null || v < min)) min = v;
            });
        });
        return min;
    }, [tourPricingSource]);
    const groupPerPersonPriceByPeriod = useMemo(() => {
        const map = new Map<number, number>();
        const groupPeriods = (tourPricingSource as Array<{ periodType?: string; periodId?: number | null; rows?: Array<{ perPersonDouble?: number | null }> }>)
            .filter((p) => p.periodType === 'group' && p.periodId != null);

        groupPeriods.forEach((period) => {
            const prices = (period.rows ?? [])
                .map((r) => (r.perPersonDouble != null ? Number(r.perPersonDouble) : null))
                .filter((v): v is number => v != null && !Number.isNaN(v));
            if (prices.length > 0) {
                map.set(Number(period.periodId), Math.min(...prices));
            }
        });
        return map;
    }, [tourPricingSource]);
    const currencySymbol = (product as { currency?: { symbol?: string; code?: string } })?.currency?.symbol ?? (product as { currency?: { code?: string } })?.currency?.code ?? '€';
    const persistGroupSelectionToCheckoutCookie = useCallback((args: { departureDate: string; periodId?: number; perPersonPrice?: number | null }) => {
        if (typeof document === 'undefined' || !product?.id) return;
        const { departureDate, periodId, perPersonPrice } = args;
        const adults = Math.max(1, checkAvailabilityAdults);
        const children = Math.max(0, checkAvailabilityChildren);
        const rooms = [{ adults, children, childAges: [] as number[] }];
        const guestCount = adults + children;
        const roomTotal = perPersonPrice != null && !Number.isNaN(Number(perPersonPrice)) ? Number(perPersonPrice) * adults : undefined;
        const payload = {
            flow: 'reservation' as const,
            product_id: Number(product.id),
            product_slug: (product as { slug?: string })?.slug ?? undefined,
            product_name: product.name ?? '',
            product_type: product.type ?? product.product_type ?? 'tour',
            return_path:
                typeof window !== 'undefined'
                    ? `${window.location.pathname}${window.location.hash || ''}`
                    : undefined,
            travel_date: departureDate,
            check_in_date: departureDate,
            guest_count: guestCount,
            child_count: children,
            rooms,
            total_price: roomTotal,
            currency: { symbol: currencySymbol, code: (product as { currency?: { code?: string } })?.currency?.code ?? undefined },
            room_prices: roomTotal != null ? [{ roomIndex: 0, total_price: roomTotal, currency: { symbol: currencySymbol } }] : undefined,
            period_id: periodId,
        };
        const maxAge = RESERVATION_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
        document.cookie = `${RESERVATION_CHECKOUT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(payload))}; path=/; max-age=${maxAge}; SameSite=Lax`;
        // Book-now sayfasında ek API isteği olmadan fiyat hesaplanabilsin; veri geldiği sayfadan (sunucudan gelen product) taşınır
        try {
            const tourPricing = tourPricingSource;
            sessionStorage.setItem(RESERVATION_PRICING_STORAGE_KEY, JSON.stringify({
                product_slug: payload.product_slug,
                product_id: payload.product_id,
                travel_date: departureDate,
                tour_pricing: Array.isArray(tourPricing) ? tourPricing : [],
                currency: payload.currency,
            }));
        } catch {
            // sessionStorage dolu veya private modda yoksa sessizce geç
        }
    }, [product, checkAvailabilityAdults, checkAvailabilityChildren, currencySymbol]);

    // Rezervasyon sözleşmeleri: SSR (initialReservationFormDetails / initialReservationFormAgreements) yoksa client-side çek
    useEffect(() => {
        if (initialReservationFormAgreements.length > 0 || initialReservationFormDetails?.agreements) {
            setReservationAgreementsLoading(false);
            return;
        }
        if (reservationFormAgreements.length > 0) {
            setReservationAgreementsLoading(false);
            return;
        }
        const productId = product?.id;
        const productType = product?.type || product?.product_type || 'tour';
        if (!productId || !productType) {
            setReservationFormAgreements([]);
            setReservationAgreementsLoading(false);
            return;
        }
        setReservationAgreementsLoading(true);
        const params = new URLSearchParams({ product_id: String(productId), product_type: productType, for_reservation: '1' });
        if (languageId) params.set('language_id', String(languageId));
        fetch(`/api/forms/details?${params.toString()}`)
            .then((res) => res.json())
            .then((data) => {
                if (data?.success && data?.data?.agreements) {
                    const required = (data.data.agreements.required || []).map((a: { id: number; title: string; slug: string; is_required?: boolean; content?: string }) => ({
                        id: a.id,
                        title: a.title,
                        slug: a.slug,
                        is_required: true,
                        content: a.content,
                    }));
                    const optional = (data.data.agreements.optional || []).map((a: { id: number; title: string; slug: string; is_required?: boolean; content?: string }) => ({
                        id: a.id,
                        title: a.title,
                        slug: a.slug,
                        is_required: false,
                        content: a.content,
                    }));
                    setReservationFormAgreements([...required, ...optional]);
                } else {
                    setReservationFormAgreements([]);
                }
            })
            .catch(() => setReservationFormAgreements([]))
            .finally(() => setReservationAgreementsLoading(false));
    }, [
        product?.id,
        product?.type,
        product?.product_type,
        languageId,
        initialReservationFormAgreements.length,
        initialReservationFormDetails,
        reservationFormAgreements.length,
    ]);

    useEffect(() => {
        if (productFaqs.length > 0) {
            setResolvedProductFaqs(productFaqs);
        }
    }, [productFaqs]);

    useEffect(() => {
        const productId = product?.id;
        if (!productId || resolvedProductFaqs.length > 0) {
            return;
        }
        const abort = new AbortController();
        const params = new URLSearchParams();
        params.set('model_type', 'Product');
        params.set('model_id', String(productId));
        if (language) {
            params.set('language', language);
        } else if (languageId) {
            params.set('language_id', String(languageId));
        }
        fetch(`/api/faqs?${params.toString()}`, { signal: abort.signal })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                const faqs: FaqEntry[] = data?.data?.general_faqs ?? data?.general_faqs ?? [];
                if (Array.isArray(faqs) && faqs.length > 0) {
                    setResolvedProductFaqs(faqs);
                }
            })
            .catch(() => {
                /* SSR/client yedek başarısız — bölüm gizli kalır */
            });
        return () => abort.abort();
    }, [product?.id, language, languageId, resolvedProductFaqs.length]);

    const tourData = useProductTourData(productForTourData, language);

    const pricingPeriodsForPriceSection = useMemo(() => {
        const rawPricing = tourPricingSource as Array<{
            periodType?: string;
            packageName?: string;
            package_name?: string;
            name?: string;
            package?: string;
            periodStart?: string;
            period_start?: string;
            start_date?: string;
            start?: string;
            periodEnd?: string;
            period_end?: string;
            end_date?: string;
            end?: string;
            rows?: Array<{
                paxRange?: string;
                pax_range?: string;
                pax?: string;
                range?: string;
                perPersonDouble?: number | string | null;
                per_person_double?: number | string | null;
                double?: number | string | null;
                per_person?: number | string | null;
                singleRoom?: number | string | null;
                single_room?: number | string | null;
                single?: number | string | null;
                single_room_price?: number | string | null;
                extraBed?: number | string | null;
                extra_bed?: number | string | null;
                extra?: number | string | null;
                extra_bed_price?: number | string | null;
                childrenFee?: number | string | null;
                children_fee?: number | string | null;
                children?: number | string | null;
                child_price?: number | string | null;
                childRules?: Array<{ min_age: number; max_age: number; fee: number }>;
                child_rules?: Array<{ min_age: number; max_age: number; fee: number }>;
            }>;
        }>;

        // Eğer periodType bilgisi varsa sadece private fiyatları göster.
        if (rawPricing.length > 0) {
            return rawPricing
                .filter((period) => (period.periodType ?? 'private') === 'private')
                .map((period) => ({
                    packageName: period.packageName || period.package_name || period.name || period.package || 'STANDARD',
                    periodStart: period.periodStart || period.period_start || period.start_date || period.start || '',
                    periodEnd: period.periodEnd || period.period_end || period.end_date || period.end || '',
                    rows: Array.isArray(period.rows) ? period.rows.map((row) => ({
                        paxRange: row.paxRange || row.pax_range || row.pax || row.range || '',
                        perPersonDouble: row.perPersonDouble != null
                            ? Number(row.perPersonDouble)
                            : Number(row.per_person_double ?? row.double ?? row.per_person ?? 0),
                        singleRoom: row.singleRoom != null
                            ? Number(row.singleRoom)
                            : Number(row.single_room ?? row.single ?? row.single_room_price ?? 0),
                        extraBed: row.extraBed != null
                            ? Number(row.extraBed)
                            : Number(row.extra_bed ?? row.extra ?? row.extra_bed_price ?? 0),
                        childrenFee: row.childrenFee != null
                            ? Number(row.childrenFee)
                            : Number(row.children_fee ?? row.children ?? row.child_price ?? 0),
                        childRules: (row.childRules ?? row.child_rules ?? []) as Array<{ min_age: number; max_age: number; fee: number }>,
                    })) : [],
                }));
        }

        // Legacy/fallback: periodType yoksa mevcut parse edilmiş veriyi kullan.
        return tourData.pricingPeriods;
    }, [tourPricingSource, tourData.pricingPeriods]);

    /** Grup tur fiyatları (Price bölümünde private tablonun üstünde gösterilir) */
    const pricingPeriodsGroupForPriceSection = useMemo(() => {
        const rawPricing = tourPricingSource as Array<{
            periodType?: string;
            packageName?: string;
            package_name?: string;
            name?: string;
            package?: string;
            periodStart?: string;
            period_start?: string;
            start_date?: string;
            start?: string;
            periodEnd?: string;
            period_end?: string;
            end_date?: string;
            end?: string;
            rows?: Array<{
                paxRange?: string;
                pax_range?: string;
                pax?: string;
                range?: string;
                perPersonDouble?: number | string | null;
                per_person_double?: number | string | null;
                double?: number | string | null;
                per_person?: number | string | null;
                singleRoom?: number | string | null;
                single_room?: number | string | null;
                single?: number | string | null;
                single_room_price?: number | string | null;
                extraBed?: number | string | null;
                extra_bed?: number | string | null;
                extra?: number | string | null;
                extra_bed_price?: number | string | null;
                childrenFee?: number | string | null;
                children_fee?: number | string | null;
                children?: number | string | null;
                child_price?: number | string | null;
                childRules?: Array<{ min_age: number; max_age: number; fee: number }>;
                child_rules?: Array<{ min_age: number; max_age: number; fee: number }>;
            }>;
        }>;
        return rawPricing
            .filter((period) => (period.periodType ?? '') === 'group')
            .map((period) => ({
                packageName: period.packageName || period.package_name || period.name || period.package || 'STANDARD',
                periodStart: period.periodStart || period.period_start || period.start_date || period.start || '',
                periodEnd: period.periodEnd || period.period_end || period.end_date || period.end || '',
                rows: Array.isArray(period.rows) ? period.rows.map((row) => ({
                    paxRange: row.paxRange || row.pax_range || row.pax || row.range || '',
                    perPersonDouble: row.perPersonDouble != null ? Number(row.perPersonDouble) : Number(row.per_person_double ?? row.double ?? row.per_person ?? 0),
                    singleRoom: row.singleRoom != null ? Number(row.singleRoom) : Number(row.single_room ?? row.single ?? row.single_room_price ?? 0),
                    extraBed: row.extraBed != null ? Number(row.extraBed) : Number(row.extra_bed ?? row.extra ?? row.extra_bed_price ?? 0),
                    childrenFee: row.childrenFee != null ? Number(row.childrenFee) : Number(row.children_fee ?? row.children ?? row.child_price ?? 0),
                    childRules: (row.childRules ?? row.child_rules ?? []) as Array<{ min_age: number; max_age: number; fee: number }>,
                })) : [],
            }));
    }, [tourPricingSource]);

    /** Günlük tur fiyatları (grup ile özel arasında; private ile aynı satır yapısı, tek/ekstra günlükte genelde yok) */
    const pricingPeriodsDailyForPriceSection = useMemo(() => {
        const rawPricing = tourPricingSource as Array<{
            periodType?: string;
            packageName?: string;
            package_name?: string;
            name?: string;
            package?: string;
            periodStart?: string;
            period_start?: string;
            start_date?: string;
            start?: string;
            periodEnd?: string;
            period_end?: string;
            end_date?: string;
            end?: string;
            rows?: Array<{
                paxRange?: string;
                pax_range?: string;
                pax?: string;
                range?: string;
                perPersonDouble?: number | string | null;
                per_person_double?: number | string | null;
                double?: number | string | null;
                per_person?: number | string | null;
                singleRoom?: number | string | null;
                single_room?: number | string | null;
                single?: number | string | null;
                single_room_price?: number | string | null;
                extraBed?: number | string | null;
                extra_bed?: number | string | null;
                extra?: number | string | null;
                extra_bed_price?: number | string | null;
                childrenFee?: number | string | null;
                children_fee?: number | string | null;
                children?: number | string | null;
                child_price?: number | string | null;
                childRules?: Array<{ min_age: number; max_age: number; fee: number }>;
                child_rules?: Array<{ min_age: number; max_age: number; fee: number }>;
            }>;
        }>;
        if (rawPricing.length === 0) {
            return [];
        }
        return rawPricing
            .filter((period) => (period.periodType ?? '') === 'daily')
            .map((period) => ({
                packageName: period.packageName || period.package_name || period.name || period.package || 'STANDARD',
                periodStart: period.periodStart || period.period_start || period.start_date || period.start || '',
                periodEnd: period.periodEnd || period.period_end || period.end_date || period.end || '',
                rows: Array.isArray(period.rows) ? period.rows.map((row) => ({
                    paxRange: row.paxRange || row.pax_range || row.pax || row.range || '',
                    perPersonDouble: row.perPersonDouble != null
                        ? Number(row.perPersonDouble)
                        : Number(row.per_person_double ?? row.double ?? row.per_person ?? 0),
                    singleRoom: row.singleRoom != null
                        ? Number(row.singleRoom)
                        : Number(row.single_room ?? row.single ?? row.single_room_price ?? 0),
                    extraBed: row.extraBed != null
                        ? Number(row.extraBed)
                        : Number(row.extra_bed ?? row.extra ?? row.extra_bed_price ?? 0),
                    childrenFee: row.childrenFee != null
                        ? Number(row.childrenFee)
                        : Number(row.children_fee ?? row.children ?? row.child_price ?? 0),
                    childRules: (row.childRules ?? row.child_rules ?? []) as Array<{ min_age: number; max_age: number; fee: number }>,
                })) : [],
            }));
    }, [tourPricingSource]);

    const { breadcrumbs } = useProductBreadcrumbs(product, language, true, pageUrls, defaultLanguage);
    const { getVariable } = useLanguageVariables();
    /** Oteller bölüm başlığı — API tab adı TR sabit olabiliyor; dil değişkeni öncelikli */
    const hotelsSectionLabel = useMemo(
        () => getVariable('tour_detail.hotels', getVariable('tour_detail_hotels', 'Hotels')),
        [getVariable]
    );

    // Information (Features) kartı: sadece en az bir alan doluysa gösterilir
    const hasFeaturesCardData = useMemo(() => {
        const { tourDuration, durationHours, tourType, minGroupSize, maxGroupSize, minAges, season, transfer, startLocation, endLocation, intermediateStops } = tourData;
        const productId = product?.metas?.product_id ? String(product.metas.product_id) : null;
        return Boolean(
            productId ||
            tourDuration ||
            (durationHours != null && durationHours !== '') ||
            (tourType != null && tourType !== '') ||
            minGroupSize != null ||
            maxGroupSize != null ||
            (minAges != null && String(minAges).trim() !== '') ||
            (season != null && season !== '') ||
            (transfer?.length ?? 0) > 0 ||
            (startLocation != null && String(startLocation).trim() !== '') ||
            (endLocation != null && String(endLocation).trim() !== '') ||
            (intermediateStops?.length ?? 0) > 0
        );
    }, [tourData, product?.metas?.product_id]);
    
    const hasServerReviews = initialReviews.length > 0;
    const { reviews: clientReviews, averageRating: clientAverageRating, reviewCount: clientReviewCount, isLoading: reviewsLoading, refetch: refetchReviews, reviewsTriggerRef } = useProductReviews(
        product?.id || null,
        languageId || undefined,
        !hasServerReviews,
        { lazyLoad: false }
    );

    const baseReviews = hasServerReviews ? initialReviews : clientReviews;
    const reviews = useMemo(() => {
        const map = new Map<number, Review>();
        [...baseReviews, ...appendedReviews].forEach((r) => map.set(r.id, r));
        return Array.from(map.values());
    }, [baseReviews, appendedReviews]);
    const averageRating = useMemo(() => {
        if (reviews.length === 0) {
            if (hasServerReviews && initialAverageRating > 0) return initialAverageRating;
            return clientAverageRating;
        }
        const sum = reviews.reduce((acc, r) => acc + (r.rating || 0), 0);
        return sum / reviews.length;
    }, [reviews, hasServerReviews, initialAverageRating, clientAverageRating]);
    
    const reviewCount = reviews.length > 0
        ? reviews.length
        : (hasServerReviews && initialReviewCount > 0 ? initialReviewCount : clientReviewCount);

    useEffect(() => {
        setAppendedReviews([]);
        setCurrentPage(1);
        setHasMoreReviews(initialHasMoreReviews);
    }, [product?.id, languageId, initialHasMoreReviews]);

    const t = useCallback((key: string, fallback?: string): string => {
        return translations[key] || getVariable(key, fallback || key);
    }, [translations, getVariable]);

    const privateSidebarHeading = useMemo(
        () => resolveSidebarFormHeading({ titleKind: 'reservation_private', t }),
        [t],
    );
    const requestSidebarHeading = useMemo(
        () =>
            resolveSidebarFormHeading({
                titleKind: 'request',
                formTitleFromApi: requestFormTitleFromApi,
                t,
            }),
        [requestFormTitleFromApi, t],
    );

    const specialGuestsMenuTitle = useMemo(
        () =>
            (product?.fields as Record<string, { field_title?: string }> | undefined)?.tab_section_special_guests?.field_title
            || t('tour_detail.special_guests', 'Special Guests'),
        [product?.fields, t],
    );

    const heritageModalGalleryUrls = useMemo(() => {
        if (!heritageModalRow) return [];
        const raw = heritageModalRow.gallery_image_urls;
        const list =
            Array.isArray(raw) && raw.length > 0
                ? raw.map((u) => transformMediaUrl(String(u))).filter((x): x is string => Boolean(x))
                : [];
        if (list.length > 0) return list;
        const cover = heritageModalRow.cover_image_url?.trim();
        return cover ? [transformMediaUrl(cover)] : [];
    }, [heritageModalRow]);

    const heritageModalTicketsPrepared = useMemo(
        () => (heritageModalRow ? heritageModalTicketLines(heritageModalRow, language, t) : []),
        [heritageModalRow, language, t],
    );

    const heritageModalWorkingHoursDisplay = useMemo(() => {
        const raw = heritageModalRow?.working_hours?.trim() ?? "";
        if (!raw) return "";
        return heritageLocalizeWorkingHoursText(raw, language);
    }, [heritageModalRow?.working_hours, language]);

    const monthOptions = useMemo(() => {
        return Array.from({ length: 12 }, (_, idx) => {
            const date = new Date(monthPickerYear, idx, 1);
            return {
                value: `${monthPickerYear}-${String(idx + 1).padStart(2, '0')}`,
                shortLabel: new Intl.DateTimeFormat(language === 'tr' ? 'tr-TR' : 'en-GB', { month: 'short' }).format(date),
                fullLabel: new Intl.DateTimeFormat(language === 'tr' ? 'tr-TR' : 'en-GB', { month: 'long', year: 'numeric' }).format(date),
                longLabel: new Intl.DateTimeFormat(language === 'tr' ? 'tr-TR' : 'en-GB', { month: 'long' }).format(date),
            };
        });
    }, [monthPickerYear, language]);

    const selectedMonthLabel = useMemo(() => {
        const [y, m] = checkAvailabilityMonth.split('-').map((v) => parseInt(v, 10));
        if (!y || !m) return checkAvailabilityMonth;
        return new Intl.DateTimeFormat(language === 'tr' ? 'tr-TR' : 'en-GB', { month: 'long', year: 'numeric' }).format(new Date(y, m - 1, 1));
    }, [checkAvailabilityMonth, language]);

    const endDateDuration = useMemo(() => {
        const parsePositiveInt = (value: unknown): number | null => {
            if (typeof value === 'number' && Number.isFinite(value) && value > 0) return Math.floor(value);
            if (typeof value === 'string') {
                const parsed = parseInt(value, 10);
                if (!Number.isNaN(parsed) && parsed > 0) return parsed;
            }
            return null;
        };
        const rawDuration = (product as { metas?: Record<string, unknown> })?.metas?.field_tour_duration as
            | { day?: unknown; night?: unknown }
            | undefined;
        const tourDataDay = parsePositiveInt(tourData.tourDuration?.day);
        const tourDataNight = parsePositiveInt(tourData.tourDuration?.night);
        const rawDay = parsePositiveInt(rawDuration?.day);
        const rawNight = parsePositiveInt(rawDuration?.night);
        return {
            nights: tourDataNight ?? rawNight,
            days: tourDataDay ?? rawDay,
        };
    }, [product, tourData.tourDuration]);

    const availableDepartureMonths = useMemo(() => {
        const set = new Set<string>();
        groupDepartureDates.forEach((d) => {
            const value = (d.departure_date || '').slice(0, 7);
            if (/^\d{4}-\d{2}$/.test(value)) set.add(value);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [groupDepartureDates]);

    const availableDepartureMonthOptions = useMemo(() => {
        if (availableDepartureMonths.length === 0) return monthOptions;
        return availableDepartureMonths.map((value) => {
            const [y, m] = value.split('-').map((v) => parseInt(v, 10));
            const date = new Date(y, (m || 1) - 1, 1);
            return {
                value,
                shortLabel: new Intl.DateTimeFormat(language === 'tr' ? 'tr-TR' : 'en-GB', { month: 'short' }).format(date),
                fullLabel: new Intl.DateTimeFormat(language === 'tr' ? 'tr-TR' : 'en-GB', { month: 'long', year: 'numeric' }).format(date),
                longLabel: new Intl.DateTimeFormat(language === 'tr' ? 'tr-TR' : 'en-GB', { month: 'long' }).format(date),
            };
        });
    }, [availableDepartureMonths, monthOptions, language]);

    const filteredGroupDepartureDates = useMemo(
        () => groupDepartureDates.filter((d) => (d.departure_date || '').startsWith(checkAvailabilityMonth)),
        [groupDepartureDates, checkAvailabilityMonth]
    );

    const mapDepartureApiRow = (d: {
        id: number;
        period_id?: number;
        departure_date: string;
        quota: number | null;
        status: string;
        discount_percentage?: number | null;
        is_bookable?: boolean;
        availability?: DepartureDateItem['availability'];
        remaining_pax?: number | null;
    }): DepartureDateItem => ({
        id: d.id,
        period_id: d.period_id,
        departure_date: d.departure_date,
        quota: d.quota,
        status: d.status,
        discount_percentage: d.discount_percentage != null ? Number(d.discount_percentage) : null,
        is_bookable: d.is_bookable,
        availability: d.availability,
        remaining_pax: d.remaining_pax ?? null,
    });

    // Çıkış tarihleri: meta güncelleme + (gerekirse) liste — tek dedupe istek
    useEffect(() => {
        if (!hasGroupPrice || !product?.id) return;
        const fromServer = Array.isArray(initialTourDepartureDates) && initialTourDepartureDates.length > 0;
        const needsFullList = !fromServer && !departureListEnrichedRef.current;

        const params = new URLSearchParams({
            product_id: String(product.id),
            language: language ?? 'en',
        });
        if (needsFullList) {
            params.set('enrich_discount_for_pax', '2');
            params.set('enrich_discount_child', '0');
            setGroupDepartureDatesLoading(true);
        }

        const cacheKey = `tour-departure-dates:${params.toString()}`;
        let alive = true;

        clientFetchJsonDedupe<{ success?: boolean; data?: Array<Parameters<typeof mapDepartureApiRow>[0]> }>(
            cacheKey,
            `/api/tour-departure-dates?${params.toString()}`,
        )
            .then((data) => {
                if (!alive || !data?.success || !Array.isArray(data.data)) return;
                const patch: Record<number, Partial<DepartureDateItem>> = {};
                data.data.forEach((row) => {
                    const mapped = mapDepartureApiRow(row);
                    patch[mapped.id] = mapped;
                });
                setDepartureMetaPatch(patch);
                if (needsFullList && data.data.length > 0) {
                    departureListEnrichedRef.current = true;
                    setGroupDepartureDatesFromClient(data.data.map(mapDepartureApiRow));
                }
            })
            .catch(() => {})
            .finally(() => {
                if (alive && needsFullList) setGroupDepartureDatesLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [hasGroupPrice, product?.id, language, initialTourDepartureDates]);

    // Grup fiyatları: seçili ay + pax (her zaman prices API)
    useEffect(() => {
        if (!hasGroupPrice || !product?.id || !checkAvailabilityMonth) {
            setGroupDeparturePrices([]);
            return;
        }
        const adults = Math.max(1, checkAvailabilityAdults);
        const children = Math.max(0, checkAvailabilityChildren);
        const params = new URLSearchParams({
            product_id: String(product.id),
            month: checkAvailabilityMonth,
            pax_count: String(adults),
            child_count: String(children),
            language: language ?? 'en',
        });
        setGroupDeparturePricesLoading(true);
        const pricesKey = `tour-departure-prices:${params.toString()}`;
        type DeparturePriceApiRow = (typeof groupDeparturePrices)[number] & {
            remaining_pax?: number | null;
            is_bookable?: boolean;
            availability?: DepartureDateItem['availability'];
        };
        clientFetchJsonDedupe<{ success?: boolean; data?: DeparturePriceApiRow[] }>(
            pricesKey,
            `/api/tour-departure-dates/prices?${params.toString()}`,
        )
            .then((data) => {
                if (data?.success && Array.isArray(data.data)) {
                    setGroupDeparturePrices(data.data);
                    const patch: Record<number, Partial<DepartureDateItem>> = {};
                    data.data.forEach((row) => {
                        if (row.id == null) return;
                        patch[row.id] = {
                            remaining_pax: row.remaining_pax ?? null,
                            is_bookable: row.is_bookable,
                            availability: row.availability,
                            discount_percentage:
                                row.discount_percentage != null ? Number(row.discount_percentage) : null,
                        };
                    });
                    if (Object.keys(patch).length > 0) {
                        setDepartureMetaPatch((prev) => ({ ...prev, ...patch }));
                    }
                } else {
                    setGroupDeparturePrices([]);
                }
            })
            .catch(() => setGroupDeparturePrices([]))
            .finally(() => setGroupDeparturePricesLoading(false));
    }, [hasGroupPrice, product?.id, checkAvailabilityMonth, checkAvailabilityAdults, checkAvailabilityChildren, language]);

    /** Her ay için grup dönemi + çıkış tarihi indirimi ile hesaplanan kişi başı fiyat (API çağrısı yok; Nisan vb. indirimli aylar doğru görünsün) */
    const priceByMonth = useMemo(() => {
        const map = new Map<string, { price: number; originalPrice: number; hasDiscount: boolean; discountPercentage: number | null }>();
        for (let m = 1; m <= 12; m++) {
            const monthValue = `${monthPickerYear}-${String(m).padStart(2, '0')}`;
            const inMonth = groupDepartureDates.filter((d) => (d.departure_date || '').startsWith(monthValue));
            if (inMonth.length === 0) continue;
            let minPrice = Infinity;
            let originalForMin: number | null = null;
            let hasDiscount = false;
            let discountPct: number | null = null;
            for (const d of inMonth) {
                const base =
                    d.period_id != null
                        ? (groupPerPersonPriceByPeriod.get(Number(d.period_id)) ?? groupMinPricePerPerson)
                        : groupMinPricePerPerson;
                if (base == null) continue;
                const pct = d.discount_percentage != null ? Number(d.discount_percentage) : 0;
                const discounted = pct > 0 ? base * (1 - pct / 100) : base;
                if (discounted < minPrice) {
                    minPrice = discounted;
                    originalForMin = base;
                    hasDiscount = pct > 0;
                    discountPct = pct > 0 ? pct : null;
                }
            }
            if (Number.isFinite(minPrice) && minPrice > 0) {
                map.set(monthValue, {
                    price: minPrice,
                    originalPrice: originalForMin ?? minPrice,
                    hasDiscount,
                    discountPercentage: discountPct,
                });
            }
        }
        return map;
    }, [monthPickerYear, groupDepartureDates, groupPerPersonPriceByPeriod, groupMinPricePerPerson]);

    const monthsWithDeparturesInPickerYear = useMemo(() => {
        const prefix = `${monthPickerYear}-`;
        return new Set(availableDepartureMonths.filter((m) => m.startsWith(prefix)));
    }, [availableDepartureMonths, monthPickerYear]);

    const totalGuestsSelected = checkAvailabilityAdults + checkAvailabilityChildren;

    const minRemainingPaxInMonth = useMemo(() => {
        const vals = filteredGroupDepartureDates
            .map((d) => d.remaining_pax)
            .filter((r): r is number => r != null && r > 0);
        if (vals.length === 0) return null;
        return Math.min(...vals);
    }, [filteredGroupDepartureDates]);

    const guestsExceedQuota = minRemainingPaxInMonth != null && totalGuestsSelected > minRemainingPaxInMonth;

    const maxGuestsAllowed = minRemainingPaxInMonth != null && minRemainingPaxInMonth > 0 ? minRemainingPaxInMonth : null;

    const clampGuestSelection = useCallback(
        (adults: number, children: number) => {
            let a = Math.max(1, adults);
            let c = Math.max(0, children);
            if (maxGuestsAllowed != null) {
                while (a + c > maxGuestsAllowed && a > 1) a -= 1;
                while (a + c > maxGuestsAllowed && c > 0) c -= 1;
            }
            return { adults: a, children: c };
        },
        [maxGuestsAllowed],
    );

    const atGuestQuotaCap =
        maxGuestsAllowed != null && checkAvailabilityAdults + checkAvailabilityChildren >= maxGuestsAllowed;

    useEffect(() => {
        if (maxGuestsAllowed == null) return;
        const total = checkAvailabilityAdults + checkAvailabilityChildren;
        if (total <= maxGuestsAllowed) return;
        let adults = checkAvailabilityAdults;
        let children = checkAvailabilityChildren;
        while (adults + children > maxGuestsAllowed && adults > 1) {
            adults -= 1;
        }
        while (adults + children > maxGuestsAllowed && children > 0) {
            children -= 1;
        }
        if (adults !== checkAvailabilityAdults) setCheckAvailabilityAdults(adults);
        if (children !== checkAvailabilityChildren) setCheckAvailabilityChildren(children);
    }, [maxGuestsAllowed, checkAvailabilityAdults, checkAvailabilityChildren]);

    const getBasePerPersonForDeparture = useCallback(
        (d: DepartureDateItem) =>
            d.period_id != null
                ? (groupPerPersonPriceByPeriod.get(Number(d.period_id)) ?? groupMinPricePerPerson)
                : groupMinPricePerPerson,
        [groupPerPersonPriceByPeriod, groupMinPricePerPerson],
    );

    // Sağ bardaki Check Availability: seçili ay/pax fiyat özeti
    const sidebarPriceSummary = useMemo(() => {
        const guestCount = Math.max(1, totalGuestsSelected);
        let best: { finalPrice: number; originalTotal: number; discountAmount: number } | null = null;

        for (const row of groupDeparturePrices) {
            const dep = filteredGroupDepartureDates.find((d) => d.departure_date === row.departure_date);
            const departureDiscountPct = row.discount_percentage ?? dep?.discount_percentage;
            const resolved = resolveGroupDeparturePrice(
                row.calculated_price,
                dep ? getBasePerPersonForDeparture(dep) : groupMinPricePerPerson,
                departureDiscountPct,
                guestCount,
            );
            if (!resolved) continue;
            const finalPrice = resolved.finalTotal ?? resolved.perPerson * guestCount;
            const originalTotal = resolved.originalPerPerson * guestCount;
            const discountAmount = Math.max(0, originalTotal - finalPrice);
            if (!best || finalPrice < best.finalPrice) {
                best = { finalPrice, originalTotal, discountAmount };
            }
        }

        if (!best) return null;
        return {
            finalPrice: best.finalPrice,
            discountAmount: best.discountAmount,
            originalTotal: best.originalTotal,
            campaignName: null as string | null,
        };
    }, [
        groupDeparturePrices,
        filteredGroupDepartureDates,
        totalGuestsSelected,
        getBasePerPersonForDeparture,
        groupMinPricePerPerson,
    ]);

    /** Seçili ay priceByMonth'da yoksa (dropdown'da) gösterilecek fallback; her iki ay seçicide de kullanılır */
    const selectedMonthFallback: SelectedMonthFallback | null = useMemo(() => {
        if (priceByMonth.has(checkAvailabilityMonth)) return null;
        if (sidebarPriceSummary) {
            const pax = Math.max(1, checkAvailabilityAdults + checkAvailabilityChildren);
            const price = sidebarPriceSummary.finalPrice / pax;
            const originalPrice = sidebarPriceSummary.originalTotal / pax;
            const hasDiscount = (sidebarPriceSummary.discountAmount ?? 0) > 0;
            let discountPercentage: number | null = null;
            if (hasDiscount && originalPrice > 0) {
                discountPercentage = Math.round((1 - price / originalPrice) * 100);
            }
            return { price, originalPrice, hasDiscount, discountPercentage };
        }
        const firstRow = groupDeparturePrices.find((r) => r.calculated_price?.success);
        const dep = firstRow
            ? filteredGroupDepartureDates.find((d) => d.departure_date === firstRow.departure_date)
            : undefined;
        const resolved = firstRow
            ? resolveGroupDeparturePrice(
                  firstRow.calculated_price,
                  dep ? getBasePerPersonForDeparture(dep) : groupMinPricePerPerson,
                  dep?.discount_percentage,
                  Math.max(1, checkAvailabilityAdults + checkAvailabilityChildren),
              )
            : null;
        if (!resolved) return null;
        return {
            price: resolved.perPerson,
            originalPrice: resolved.originalPerPerson,
            hasDiscount: resolved.hasDiscount,
            discountPercentage: resolved.discountPercentage,
        };
    }, [
        checkAvailabilityMonth,
        priceByMonth,
        sidebarPriceSummary,
        groupDeparturePrices,
        filteredGroupDepartureDates,
        getBasePerPersonForDeparture,
        groupMinPricePerPerson,
        checkAvailabilityAdults,
        checkAvailabilityChildren,
    ]);

    /** Grup tur: seçili ay çıkış yoksa en erken çıkış ayına geç (year picker ile senkron) */
    useEffect(() => {
        if (!hasGroupPrice || availableDepartureMonths.length === 0) return;
        if (availableDepartureMonths.includes(checkAvailabilityMonth)) return;

        const earliest = availableDepartureMonths[0];
        if (!earliest || earliest === checkAvailabilityMonth) return;

        setCheckAvailabilityMonth(earliest);
        const y = parseInt(earliest.slice(0, 4), 10);
        if (!Number.isNaN(y)) setMonthPickerYear(y);
    }, [hasGroupPrice, availableDepartureMonths, checkAvailabilityMonth]);

    // Sağ bardaki fiyat özeti etiketleri: sayfa diline göre (LanguageVariables/translations yoksa language prop ile garanti)
    const sidebarPriceLabels = useMemo(() => {
        const keys = {
            loading_prices: 'tour_detail.loading_prices',
            original_total: 'tour_detail.original_total',
            discount: 'tour_detail.discount',
            total: 'tour_detail.total',
            price_based_on_guests: 'tour_detail.price_based_on_guests',
        } as const;
        const fallback: Record<string, { tr: string; en: string }> = {
            loading_prices: { tr: 'Fiyatlar hesaplanıyor...', en: 'Calculating prices...' },
            original_total: { tr: 'Orijinal toplam', en: 'Original total' },
            discount: { tr: 'İndirim', en: 'Discount' },
            total: { tr: 'Toplam', en: 'Total' },
            price_based_on_guests: { tr: 'Fiyat seçilen kişi ve çocuk sayısına göredir.', en: 'Price is based on selected number of adults and children.' },
        };
        const lang = language === 'tr' ? 'tr' : 'en';
        return {
            loading_prices: translations[keys.loading_prices] || getVariable(keys.loading_prices, fallback.loading_prices[lang]) || fallback.loading_prices[lang],
            original_total: translations[keys.original_total] || getVariable(keys.original_total, fallback.original_total[lang]) || fallback.original_total[lang],
            discount: translations[keys.discount] || getVariable(keys.discount, fallback.discount[lang]) || fallback.discount[lang],
            total: translations[keys.total] || getVariable(keys.total, fallback.total[lang]) || fallback.total[lang],
            price_based_on_guests: translations[keys.price_based_on_guests] || getVariable(keys.price_based_on_guests, fallback.price_based_on_guests[lang]) || fallback.price_based_on_guests[lang],
        };
    }, [language, translations, getVariable]);

    // Meal translations - sadece dil çeviri sisteminden (fallback yok)
    const getMealLabel = useCallback((mealKey: string): string => {
        const translationKey = `tour_detail.meal.${mealKey}`;
        const translated = getVariable(translationKey, '');
        return translated || translationKey;
    }, [getVariable]);

    // Sağ bar genişliği: Check availability görünüyorsa lg:col-span-3, Request form görünüyorsa lg:col-span-4
    const sidebarLayoutClass = (hasGroupPrice && groupDepartureDates.length > 0)
        ? 'lg:col-start-10 lg:col-span-3'
        : 'lg:col-span-4';

    const contentLayoutClass = (hasGroupPrice && groupDepartureDates.length > 0)
        ? 'lg:col-span-9'
        : 'lg:col-span-8';
    // Check favorite status from backend
    useEffect(() => {
        if (!product?.id || !isAuthenticated || !customer) {
            setIsFavorite(false);
            return;
        }

        const checkFavorite = async () => {
            try {
                const productType = product?.type || product?.product_type || 'tour';
                const result = await CustomerApiService.checkFavorite(product.id!, productType);
                setIsFavorite(result.isFavorite);
            } catch (error) {
                // Silently fail - user might not be logged in
                setIsFavorite(false);
            }
        };

        checkFavorite();
    }, [product?.id, product?.type, product?.product_type, isAuthenticated, customer]);

    const toggleFavorite = useCallback(async () => {
        if (!product?.id || !isAuthenticated || !customer) {
            // If not authenticated, show login prompt or redirect
            const loginUrl = resolveUrlFromMap(
                pageUrls,
                'login',
                language,
                defaultLanguage ?? 'en',
                `/${language}/login`
            );
            const returnPath = typeof window !== 'undefined' ? window.location.pathname : '';
            router.push(`${loginUrl}?return=${encodeURIComponent(returnPath)}`);
            return;
        }

        if (isFavoriteLoading) return;

        setIsFavoriteLoading(true);
        try {
            const productType = product?.type || product?.product_type || 'tour';
            const result = await CustomerApiService.toggleFavorite(product.id, productType);
            setIsFavorite(result.isFavorite);
        } catch (error) {
            console.error('[ProductTourDetail] Toggle favorite error:', error);
            // Show error message to user
            const errorMessage = error instanceof Error ? error.message : t('tour_detail.favorite_error', 'Failed to update favorite');
            alert(errorMessage);
        } finally {
            setIsFavoriteLoading(false);
        }
    }, [isFavorite, isFavoriteLoading, product?.id, product?.type, product?.product_type, isAuthenticated, customer, language, pageUrls, t, router, defaultLanguage]);

    const transformHtmlImageUrls = useCallback((html: string): string => {
        if (!html) return html;
        return html.replace(/<img([^>]*?)src=["']([^"']+?)["']([^>]*?)>/gi, (match, beforeSrc, srcUrl, afterSrc) => {
            const transformedUrl = transformMediaUrl(srcUrl);
            return `<img${beforeSrc}src="${transformedUrl}"${afterSrc}>`;
        });
    }, []);

    const hasValidCoordinates = useCallback((lat: any, lng: any): boolean => {
        return lat !== undefined && lat !== null && !isNaN(Number(lat)) && isFinite(Number(lat)) &&
               lng !== undefined && lng !== null && !isNaN(Number(lng)) && isFinite(Number(lng));
    }, []);

    const hasLocationData = useCallback((day: any): boolean => {
        if (Array.isArray(day.stops) && day.stops.length > 0) {
            for (const s of day.stops) {
                if (!s) continue;
                if (hasValidCoordinates(s.lat, s.lng)) return true;
                if (s.location_address && String(s.location_address).trim().length > 0) return true;
            }
        }
        return hasValidCoordinates(day.lat, day.lng) ||
               (day.location_address && day.location_address.trim().length > 0);
    }, [hasValidCoordinates]);

    const tourDaysWithImages = useMemo(() => 
        tourData.tourProgram.map(day => ({
            ...day,
            description: day.description
                ? stripTourProgramTypography(transformHtmlImageUrls(day.description))
                : day.description,
            images: day.images || []
        })), 
        [tourData.tourProgram, transformHtmlImageUrls]
    );

    const mapDays = useMemo(() => 
        tourDaysWithImages.filter(hasLocationData), 
        [tourDaysWithImages, hasLocationData]
    );

    const mapLocationsForMap = useMemo(
        () => buildTourMapLocations(mapDays),
        [mapDays],
    );

    const dayFirstMapIndex = useMemo(
        () => buildDayFirstMapIndex(mapLocationsForMap),
        [mapLocationsForMap],
    );

    /** Story Mode ile harita aynı durak listesini kullanır */
    const mapStoryPoints = useMemo(() => {
        const dayById = new Map(mapDays.map((day) => [day.id, day]));
        return mapLocationsForMap.map((loc, index) => {
            const day = loc.day_id ? dayById.get(loc.day_id) : undefined;
            const description = firstNonEmpty(
                day?.description,
                loc.location_description,
                day?.location_description,
            ) ?? '';
            return {
                id: `${loc.day_id}-${index}`,
                lat: loc.lat ?? 0,
                lng: loc.lng ?? 0,
                title: loc.location_title,
                description,
                image: loc.image ?? day?.images?.[0] ?? '',
                day: loc.day_number,
                stopIndex: loc.order_index,
                dayStopIndex: loc.day_stop_index,
                dayStopTotal: loc.day_stop_total,
                dayId: loc.day_id,
            };
        });
    }, [mapLocationsForMap, mapDays]);

    const highlightedDayId = mapActiveIndex != null
        ? mapLocationsForMap[mapActiveIndex]?.day_id ?? null
        : null;

    const scrollToItineraryDay = useCallback((dayId: string) => {
        setAllExpanded(false);
        setOpenTourDay(dayId);
        requestAnimationFrame(() => {
            const el = document.querySelector(`[data-day-id="${dayId}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, []);

    const handleMapMarkerClick = useCallback((index: number) => {
        const loc = mapLocationsForMap[index];
        if (!loc) return;
        setMapActiveIndex(index);
        mapRef.current?.focusMarkerIndex(index);
        if (loc.day_id) {
            scrollToItineraryDay(loc.day_id);
        }
    }, [mapLocationsForMap, scrollToItineraryDay]);

    const focusMapOnDay = useCallback((dayId: string) => {
        const idx = dayFirstMapIndex.get(dayId);
        if (idx == null) return;
        setMapActiveIndex(idx);
        mapRef.current?.focusMarkerIndex(idx);
        if (typeof window !== 'undefined' && window.innerWidth < 1024) {
            mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [dayFirstMapIndex]);

    const handleStoryModeChange = useCallback((active: boolean) => {
        setIsStoryModeActive(active);
        if (active && mapStoryPoints.length > 0) {
            setMapActiveIndex(0);
        } else {
            setMapActiveIndex(null);
        }
    }, [mapStoryPoints.length]);

    const handleStoryIndexChange = useCallback((index: number) => {
        setMapActiveIndex(index);
        mapRef.current?.focusMarkerIndex(index);
        const point = mapStoryPoints[index];
        if (point?.dayId) {
            scrollToItineraryDay(point.dayId);
        }
    }, [mapStoryPoints, scrollToItineraryDay]);

    const hasMapLocations = mapLocationsForMap.length > 0;

    useEffect(() => {
        const [y] = checkAvailabilityMonth.split('-');
        const parsedYear = parseInt(y, 10);
        if (!Number.isNaN(parsedYear)) {
            setMonthPickerYear(parsedYear);
        }
    }, [checkAvailabilityMonth]);

    useEffect(() => {
        if (!isMonthPickerOpen) return;
        const onDocClick = (e: MouseEvent) => {
            if (!monthPickerRef.current) return;
            if (!monthPickerRef.current.contains(e.target as Node)) {
                setIsMonthPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [isMonthPickerOpen]);

    useEffect(() => {
        if (!isDatesSectionMonthPickerOpen) return;
        const onDocClick = (e: MouseEvent) => {
            if (!datesSectionMonthPickerRef.current) return;
            if (!datesSectionMonthPickerRef.current.contains(e.target as Node)) {
                setIsDatesSectionMonthPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [isDatesSectionMonthPickerOpen]);

    // minPax: metas.min_group_size veya fiyat tablosunun ilk satırından (örn. "3 People" → 3)
    const minPaxForReservation = useMemo(() => {
        if (tourData.minGroupSize != null && tourData.minGroupSize > 0) return tourData.minGroupSize;
        const firstRow = tourData.pricingPeriods[0]?.rows?.[0];
        const paxRange = firstRow?.paxRange;
        if (paxRange) {
            const match = String(paxRange).match(/^(\d+)/);
            return match ? parseInt(match[1], 10) : null;
        }
        return null;
    }, [tourData.minGroupSize, tourData.pricingPeriods]);

    const hotelEntries = useMemo(() => extractOrderedHotelEntries(tourHotelsMeta), [tourHotelsMeta]);

    const hotelIds = useMemo(() => extractOrderedHotelIds(tourHotelsMeta), [tourHotelsMeta]);

    const hotelIdsKey = useMemo(() => hotelIds.join(','), [hotelIds]);

    const orderedHotels = useMemo(
        () => sortHotelsByIdOrder(hotels, hotelIds),
        [hotels, hotelIds],
    );

    // tour_hotels meta'sından otel satırları (paket/gece — aynı otel farklı paketlerde tekrarlanabilir)
    // hotelEntries: extractOrderedHotelEntries(tourHotelsMeta) yukarıda

    // Otel verisi: sayfa verisinde embed ile geldiyse client fetch yok; yoksa sekme/scroll'da lazy fetch
    useEffect(() => {
        if (initialTourHotels != null && initialTourHotels.length > 0) return;
        if (hotelIds.length === 0 || !hotelsFetchTriggered) {
            if (!hotelsFetchTriggered) return;
            setHotels([]);
            setHotelsLoading(false);
            return;
        }

        let cancelled = false;
        setHotelsLoading(true);

        const fetchHotels = async () => {
            try {
                const controller = new AbortController();
                const timeoutMs = 60000; // 60s (Next.js API -> Laravel zinciri yavaş olabiliyor)
                const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

                const response = await fetch(`/api/products?ids=${hotelIdsKey}&type=hotel&language=${language}`, {
                    cache: 'force-cache',
                    signal: controller.signal,
                });
                clearTimeout(timeoutId);
                if (cancelled) return;
                if (!response.ok) {
                    if (!cancelled) {
                        setHotels([]);
                        setHotelsLoading(false);
                    }
                    return;
                }
                let data: { data?: unknown[] } = {};
                try {
                    data = await response.json();
                } catch {
                    if (!cancelled) {
                        setHotels([]);
                        setHotelsLoading(false);
                    }
                    return;
                }
                if (cancelled) return;
                // API bazen success döndürmüyor; data.data array ise kabul et
                if (data.data && Array.isArray(data.data)) {
                    const validHotels = data.data.filter((hotel: any) => hotel?.name);
                    if (!cancelled) {
                        setHotels(sortHotelsByIdOrder(validHotels as Array<{ id: number }>, hotelIds));
                        setHotelsLoading(false);
                    }
                } else {
                    if (!cancelled) {
                        setHotels([]);
                        setHotelsLoading(false);
                    }
                }
            } catch {
                if (!cancelled) {
                    setHotels([]);
                    setHotelsLoading(false);
                }
            }
        };

        fetchHotels().catch(() => {
            if (!cancelled) {
                setHotels([]);
                setHotelsLoading(false);
            }
        });
        return () => {
            cancelled = true;
            setHotelsLoading(false);
        };
    }, [hotelIdsKey, language, hotelsFetchTriggered, initialTourHotels, hotelIds]);

    // Otel sekmesine scroll ile gelindiğinde fetch tetikle (lazy load)
    useEffect(() => {
        if (hotelIds.length === 0) return;
        const el = document.getElementById('price-services-hotel');
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) setHotelsFetchTriggered(true);
            },
            { rootMargin: '100px', threshold: 0 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [hotelIds.length]);

    useEffect(() => {
        if (hotelIds.length === 0) return;
        const onHash = () => {
            if (typeof window !== 'undefined' && (window.location?.hash === '#price-services-hotel' || window.location?.hash === '#tour_hotels')) setHotelsFetchTriggered(true);
        };
        onHash();
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, [hotelIds.length]);

    /** Hash yoksa ilk boyamada üstte başla (restore/odak kaynaklı alta atlama riskini azaltır). */
    useLayoutEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.location.hash) return;
        window.scrollTo(0, 0);
    }, []);

    const scrollToPageSection = useCallback((sectionId: string) => {
        if (typeof window === 'undefined' || !sectionId) return;
        const run = () => {
            document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };
        requestAnimationFrame(() => requestAnimationFrame(run));
    }, []);

    // Hash ile gelindiğinde (#itinerary, #dates-and-availability) ilgili bölüme scroll
    useEffect(() => {
        if (typeof window === 'undefined' || !window.location?.hash) return;
        const id = window.location.hash.slice(1);
        if (!id) return;
        scrollToPageSection(id);
    }, [scrollToPageSection, groupDepartureDates.length, tourDaysWithImages.length]);

    const handleTourDayToggle = useCallback((id: string) => {
        setOpenTourDay(prev => prev === id ? null : id);
    }, []);

    const handleExpandAll = useCallback(() => {
        if (allExpanded) {
            setOpenTourDay(null);
            setAllExpanded(false);
        } else {
            const lastDayId = tourDaysWithImages[tourDaysWithImages.length - 1]?.id;
            if (lastDayId) setOpenTourDay(lastDayId);
            setAllExpanded(true);
        }
    }, [allExpanded, tourDaysWithImages]);

    const itineraryGalleryAnchorCount = useMemo(() => {
        return tourDaysWithImages.reduce((n, day) => n + (day.images?.length ?? 0), 0);
    }, [tourDaysWithImages]);

    useEffect(() => {
        const root = itineraryFancyboxRef.current;
        if (typeof window === "undefined" || !root || itineraryGalleryAnchorCount === 0) return;

        let cancelled = false;
        const frame = requestAnimationFrame(() => {
            if (cancelled || !itineraryFancyboxRef.current) return;
            const bindRoot = itineraryFancyboxRef.current;
            const hasAnchors = bindRoot.querySelector('a[data-fancybox^="itinerary-day-"]');
            if (!hasAnchors) return;

            try {
                Fancybox.unbind(bindRoot);
                Fancybox.bind(bindRoot, 'a[data-fancybox^="itinerary-day-"]', {
                    Image: { zoom: true, wheel: "slide" },
                } as any);
            } catch {
                // Geri navigasyon / bfcache sonrası Fancybox durumu bozulmuş olabilir
            }
        });

        const onReveal = () => {
            document.body.style.overflow = "hidden";
        };
        const onDestroy = () => {
            document.body.style.overflow = "";
        };
        root.addEventListener("fancybox:reveal", onReveal);
        root.addEventListener("fancybox:destroy", onDestroy);

        return () => {
            cancelled = true;
            cancelAnimationFrame(frame);
            root.removeEventListener("fancybox:reveal", onReveal);
            root.removeEventListener("fancybox:destroy", onDestroy);
            try {
                Fancybox.unbind(root);
                Fancybox.close();
            } catch {
                document.body.style.overflow = "";
            }
        };
    }, [itineraryGalleryAnchorCount, openTourDay, allExpanded]);

    /** Sayfadaki bölümlerin DOM sırası (içerik sırası). Menü ve scroll göstergesi bu sıraya göre hizalanır. */
    const contentOrderSections = useMemo(() => {
        const hasInformationContent = (product?.description || tourData.relevantForYou || tourData.whatIsExpected) && !tourData.information.content;
        const hasServices = (tourData.includedServices.length > 0 || tourData.excludedServices.length > 0);
        const sections: Array<{ id: string; label: string }> = [];
        const sectionIds = new Set<string>();

        const push = (id: string, label: string) => {
            if (sectionIds.has(id)) return;
            sectionIds.add(id);
            sections.push({ id, label });
        };

        // DOM sırası: features → information → special-guests → itinerary → …
        if (hasFeaturesCardData) push('features', t('features', 'Features'));
        if (hasInformationContent) push('information', t('information', 'Information'));
        if (showSpecialGuestsSection) push('special-guests', specialGuestsMenuTitle);
        if (tourDaysWithImages.length > 0) push('itinerary', t('itinerary', 'Itinerary'));
        const showPriceSection =
            pricingPeriodsForPriceSection.length > 0 ||
            pricingPeriodsGroupForPriceSection.length > 0 ||
            pricingPeriodsDailyForPriceSection.length > 0 ||
            pricingPending ||
            pricingLoadFailed ||
            (effectiveHasPrice ?? product?.has_price ?? false);
        if (showPriceSection) push('pricing', t('price', 'Price'));
        if (!hasGroupPrice && tourData.departureDates.length > 0) push('departure_dates', t('departure_dates', 'Departure Dates'));
        if (hasGroupPrice && groupDepartureDates.length > 0) push('dates-and-availability', t('tour_detail.dates', 'Dates'));
        if (hasServices) push('services', t('services', 'Services'));
        if (hotelIds.length > 0) {
            push('price-services-hotel', hotelsSectionLabel);
        }
        if (showHeritageSitesSection) push('heritage_sites', t('tour_detail.heritage_sites', 'Heritage sites'));
        tourData.tabFields.forEach(tabGroup => {
            if (tabGroup.sectionId === 'tour_hotels') return;
            push(
                tabGroup.sectionId,
                resolveRepeaterSectionTitle(tabGroup.tabName, tabGroup.tabName, t),
            );
        });
        if (resolvedProductFaqs.length > 0) push('faqs', t('faqs', 'FAQs'));
        push('reviews', t('reviews', 'Reviews'));

        return sections;
    }, [tourData.tabFields, pricingPeriodsForPriceSection.length, pricingPeriodsGroupForPriceSection.length, pricingPeriodsDailyForPriceSection.length, pricingPending, pricingLoadFailed, effectiveHasPrice, product?.has_price, tourData.includedServices, tourData.excludedServices, tourDaysWithImages, hotelIds.length, tourData.departureDates.length, resolvedProductFaqs, product, tourData, t, hasFeaturesCardData, hasGroupPrice, groupDepartureDates.length, hotelsSectionLabel, showHeritageSitesSection, showSpecialGuestsSection, specialGuestsMenuTitle]);

    const scrollSections = contentOrderSections;

    // Diğer Açıklamalar (iptal koşulu + repeater maddeler) – menüde dropdown
    const otherDescriptionsGroups = useMemo(
        () => tourData.tabFields.filter((tab) => isOtherDescriptionsGroup(tab.groupKey)),
        [tourData.tabFields]
    );

    /** Overview ("Diğer Açıklamalar") akordiyon öğeleri: tabFields'teki other_descriptions grupları + backend tour_static (dil/aktiflik filtresi backend'de yapılır). */
    const overviewAccordionItems = useMemo<OverviewAccordionItem[]>(() => {
        const items: OverviewAccordionItem[] = [];
        otherDescriptionsGroups.forEach((tab) => {
            (tab.fields ?? []).forEach((field) => {
                const content = field.content ?? '';
                if (!content || (typeof content === 'string' && content.trim() === '')) return;
                items.push({
                    sectionId: tab.sectionId,
                    title: resolveRepeaterSectionTitle(tab.tabName, tab.tabName, t),
                    contentHtml: content,
                });
            });
        });
        const tourStatic = product?.tour_static;
        if (Array.isArray(tourStatic)) {
            tourStatic.forEach((entry) => {
                const content = entry?.description ?? '';
                if (!content || content.trim() === '') return;
                items.push({
                    sectionId: `tour-static-${entry.id}`,
                    title: entry.name || '',
                    contentHtml: content,
                });
            });
        }
        return items;
    }, [otherDescriptionsGroups, product?.tour_static, t]);

    // Menü: Book now (private) en sonda; Oteller hotelIds varken; Price sadece fiyat varken; Diğer Açıklamalar dropdown
    const displayMenuItems = useMemo(() => {
        let items = menuItems ?? [];
        if (!items.length && !overviewAccordionItems.length && !hasBookablePrivateOrDailyPrice) return items;
        items = items
            .filter((item) => {
                if (item.link === '#heritage_sites') return showHeritageSitesSection;
                if (item.link === '#special-guests') return showSpecialGuestsSection;
                if (item.link === '#tour_hotels' || item.link === '#price-services-hotel') return hotelIds.length > 0;
                if (item.link === '#departure_dates') return !hasGroupPrice && tourData.departureDates.length > 0;
                if (item.link === '#pricing') {
                    return (
                        pricingPeriodsForPriceSection.length > 0 ||
                        pricingPeriodsGroupForPriceSection.length > 0 ||
                        pricingPeriodsDailyForPriceSection.length > 0 ||
                        pricingPending ||
                        pricingLoadFailed ||
                        (effectiveHasPrice ?? product?.has_price ?? false)
                    );
                }
                if (item.link === '#dates-and-availability') return hasGroupPrice && groupDepartureDates.length > 0;
                if (item.link === '#features') return hasFeaturesCardData;
                if (item.link === '#faqs') return resolvedProductFaqs.length > 0;
                return true;
            })
            .map((item) => {
                if (item.link === '#pricing') {
                    return { ...item, title: t('price', 'Price') };
                }
                if (item.link === '#tour_hotels') {
                    return { ...item, link: '#price-services-hotel' };
                }
                return item;
            });

        const priceSectionVisible =
            pricingPeriodsForPriceSection.length > 0 ||
            pricingPeriodsGroupForPriceSection.length > 0 ||
            pricingPeriodsDailyForPriceSection.length > 0 ||
            pricingPending ||
            pricingLoadFailed ||
            (effectiveHasPrice ?? product?.has_price ?? false);

        if (priceSectionVisible && !items.some((i) => i.link === '#pricing')) {
            const pricingMenuItem: MenuItems = {
                title: t('price', 'Price'),
                link: '#pricing',
                svg: '',
            };
            const itineraryIdx = items.findIndex((i) => i.link === '#itinerary');
            const insertAt = itineraryIdx >= 0 ? itineraryIdx + 1 : items.length;
            items = [...items.slice(0, insertAt), pricingMenuItem, ...items.slice(insertAt)];
        }

        if (hasGroupPrice && groupDepartureDates.length > 0) {
                const existingDatesAvailability = items.find((i) => i.link === '#dates-and-availability');
                const datesAvailabilityItem: MenuItems = existingDatesAvailability ?? {
                    title: t('tour_detail.dates', 'Dates'),
                    link: '#dates-and-availability',
                    svg: '',
                };
                items = items.filter((i) => i.link !== '#dates-and-availability');
                const departureIdx = items.findIndex((i) => i.link === '#departure_dates');
                const pricingIdx = items.findIndex((i) => i.link === '#pricing');
                const itineraryIdx = items.findIndex((i) => i.link === '#itinerary');
                const infoIdx = items.findIndex((i) => i.link === '#information' || i.link === '#definition');
                const insertAt =
                    departureIdx >= 0 ? departureIdx + 1 :
                    pricingIdx >= 0 ? pricingIdx + 1 :
                    itineraryIdx >= 0 ? itineraryIdx + 1 :
                    infoIdx >= 0 ? infoIdx + 1 :
                    0;
                items = [...items.slice(0, insertAt), datesAvailabilityItem, ...items.slice(insertAt)];
            }
        // Departure Dates menü öğesi: private Price sonrası, Book now öncesi
        if (!hasGroupPrice && tourData.departureDates.length > 0) {
            const existingDeparture = items.find((i) => i.link === '#departure_dates');
            const departureItem: MenuItems = existingDeparture ?? {
                title: t('departure_dates', 'Departure Dates'),
                link: '#departure_dates',
                svg: '',
            };
            items = items.filter((i) => i.link !== '#departure_dates');
            const afterPricing = items.findIndex((i) => i.link === '#pricing');
            const afterItinerary = items.findIndex((i) => i.link === '#itinerary');
            const insertAt = afterPricing >= 0 ? afterPricing + 1 : (afterItinerary >= 0 ? afterItinerary + 1 : 0);
            items = [...items.slice(0, insertAt), departureItem, ...items.slice(insertAt)];
        }

        if (resolvedProductFaqs.length > 0 && !items.some((i) => i.link === '#faqs')) {
            const faqsMenuItem: MenuItems = {
                title: t('faqs', 'FAQs'),
                link: '#faqs',
                svg: '',
            };
            const reviewsIdx = items.findIndex((i) => i.link === '#reviews');
            const insertAt = reviewsIdx >= 0 ? reviewsIdx : items.length;
            items = [...items.slice(0, insertAt), faqsMenuItem, ...items.slice(insertAt)];
        }

        if (overviewAccordionItems.length > 0) {
            const children = overviewAccordionItems.map((item) => ({
                title: item.title,
                link: `#${item.sectionId}`,
            }));

            const otherDescriptionsItem: MenuItems = {
                title: t('other_descriptions', 'Diğer Açıklamalar'),
                link: `#${overviewAccordionItems[0].sectionId}`,
                svg: '',
                isDropdown: true,
                children,
            };
            // İlk yerleştirme: ören yerlerinden sonra (DOM’da services → heritage_sites → tabFields); yoksa services sonrası. Nihai sıra contentOrderSections ile sort’ta düzelir.
            const afterHeritage = items.findIndex((i) => i.link === '#heritage_sites');
            const afterServices = items.findIndex((i) => i.link === '#services');
            const idx =
                afterHeritage >= 0 ? afterHeritage + 1 : afterServices >= 0 ? afterServices + 1 : items.length;
            items = [...items.slice(0, idx), otherDescriptionsItem, ...items.slice(idx)];
        }
        // Book now (private) en sonda
        if (hasBookablePrivateOrDailyPrice) {
            const bookNowItem: MenuItems = {
                title: t('tour_detail.book_now'),
                link: '#',
                svg: '',
                actionType: 'open_reservation_modal',
                tooltip: t('tour_detail.book_now_private_tooltip', 'Private Tour'),
            };
            items = [...items, bookNowItem];
        }
        // Menüyü sayfadaki içerik (DOM) sırasına göre sırala; scroll göstergesi ile aynı sıra
        const orderIds = contentOrderSections.map((s) => s.id);
        const getOrderIndex = (item: MenuItems): number => {
            if (item.actionType === 'open_reservation_modal') return orderIds.length + 1;
            if (item.isDropdown && item.children?.length) {
                const indices = item.children
                    .map((c) => orderIds.indexOf((c.link ?? '').replace(/^#/, '')))
                    .filter((i) => i >= 0);
                return indices.length > 0 ? Math.min(...indices) : orderIds.length + 1;
            }
            const id = (item.link ?? '').replace(/^#/, '');
            const idx = orderIds.indexOf(id);
            return idx >= 0 ? idx : orderIds.length + 1;
        };
        items = [...items].sort((a, b) => getOrderIndex(a) - getOrderIndex(b));
        return items;
    }, [menuItems, hasBookablePrivateOrDailyPrice, hotelIds.length, tourData.departureDates.length, pricingPeriodsForPriceSection.length, pricingPeriodsGroupForPriceSection.length, pricingPeriodsDailyForPriceSection.length, pricingPending, pricingLoadFailed, effectiveHasPrice, product?.has_price, overviewAccordionItems, t, hasFeaturesCardData, hasGroupPrice, groupDepartureDates.length, contentOrderSections, showHeritageSitesSection, showSpecialGuestsSection, resolvedProductFaqs.length]);
    return (
        <motion.div 
            className="w-full max-w-full min-w-0 xl:w-9/12 mx-auto px-3 sm:px-4 py-8 sm:py-12 lg:py-16 relative z-10" 
            initial="hidden" 
            animate="visible" 
            variants={pageVariants}
        >
            {product?.id != null && (
                <BrochureDownloadModal
                    isOpen={brochureModalOpen}
                    onClose={() => setBrochureModalOpen(false)}
                    productId={product.id}
                    productType="tour"
                    source="product_detail"
                    prefillEmail={customer?.email ?? null}
                    languageId={languageId ?? undefined}
                />
            )}
            {/* Book now modal: broşür modalı ile aynı yapı (AnimatePresence, backdrop, dialogVariants). Sadece rezervasyon formu gösterilir. */}
            <AnimatePresence>
                {reservationModalOpen && (hasBookablePrivateOrDailyPrice || pricingPending) && product?.id != null && (
                    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-3 sm:p-4">
                        <motion.div
                            className="absolute inset-0 bg-black/50"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setReservationModalOpen(false)}
                            aria-hidden
                        />
                        <motion.div
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="reservation-modal-title"
                            className="relative mt-2 sm:mt-0 bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[92dvh] overflow-hidden flex flex-col"
                            variants={dialogVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl shrink-0">
                                <h2 id="reservation-modal-title" className="text-lg font-semibold text-gray-900">
                                    {reservationPricingPeriodType === 'daily' ? t('tour_detail.sidebar_reservation_daily', 'Reservation') : t('tour_detail.private_book_now', 'Private Book now')}
                                </h2>
                                <button
                                    type="button"
                                    onClick={() => setReservationModalOpen(false)}
                                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                                    aria-label={t('tour_detail.close', 'Close')}
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-5 sm:p-6 overflow-y-auto overscroll-contain">
                                {pricingPending ? (
                                    <ReservationBookingSidebarSkeleton compact />
                                ) : (
                                <ReservationBookingSidebar
                                    productId={product.id}
                                    productType={product?.type || product?.product_type || 'tour'}
                                    productName={product?.name}
                                    hasPrice={effectiveHasPrice ?? (Array.isArray(tourPricingSource) && tourPricingSource.length > 0)}
                                    bookingFlow={((product as { booking_flow?: 'request_first' | 'instant_online' | 'bank_transfer_only' })?.booking_flow) ?? 'request_first'}
                                    bookingFlowConfigured={(product as { booking_flow_configured?: boolean })?.booking_flow_configured ?? false}
                                    language={language}
                                    languageId={languageId || undefined}
                                    agreements={reservationFormAgreements}
                                    agreementsLoading={reservationAgreementsLoading}
                                    requestFormTitle={requestFormTitleFromApi}
                                    translations={translations}
                                    minPax={minPaxForReservation}
                                    initialRequestFormDetails={initialRequestFormDetails}
                                    packages={(() => {
                                        const mode = reservationPricingPeriodType;
                                        const periods = (tourPricingSource as TourPricingPeriod[]).filter(
                                            (p) => (p.periodType ?? 'private') === mode,
                                        );
                                        const names = [...new Set(periods.map((p) => p.packageName ?? 'STANDARD'))];
                                        return names.map((packageName) => ({ packageName }));
                                    })()}
                                    onReservationSubmit={() => setReservationModalOpen(false)}
                                    showAsReservationForm={true}
                                    tourPricing={(tourPricingSource.length > 0 ? (tourPricingSource as TourPricingPeriod[]) : undefined)}
                                    currency={(product as { currency?: { code: string; symbol: string; symbol_position?: 'before' | 'after'; thousands_separator?: string; decimal_separator?: string } })?.currency}
                                    productMetas={(product as { metas?: { child_pax_age_threshold_private?: number; min_ages?: string | number | null } })?.metas}
                                    pricingPeriodType={reservationPricingPeriodType}
                                />
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
            {requestFormModalOpen && product?.id != null && (
                <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-0 sm:p-4 bg-black/50" role="dialog" aria-modal="true" aria-labelledby="request-modal-title">
                    <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl max-w-lg w-full max-h-[90dvh] sm:max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center">
                            <h2 id="request-modal-title" className="text-lg font-bold text-alsero-dark-gray">{requestSidebarHeading}</h2>
                            <button type="button" onClick={() => setRequestFormModalOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600" aria-label="Close">×</button>
                        </div>
                        <div className="p-4 overflow-y-auto overscroll-contain min-h-0 flex-1">
                            <RequestForm
                                initialFormDetails={initialRequestFormDetails ?? null}
                                productId={product.id}
                                productType={product?.type || product?.product_type || 'tour'}
                                productName={product?.name}
                                language={language}
                                languageId={languageId ?? undefined}
                            />
                        </div>
                    </div>
                </div>
            )}

            <AnimatePresence>
                {heritageModalRow && (
                    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-3 sm:p-4">
                        <motion.div
                            className="absolute inset-0 bg-black/50"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setHeritageModalRow(null)}
                            aria-hidden
                        />
                        <motion.div
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="heritage-site-modal-title"
                            className="relative mt-2 sm:mt-6 bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[92dvh] overflow-hidden flex flex-col"
                            variants={dialogVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between gap-3 rounded-t-2xl shrink-0">
                                <h2 id="heritage-site-modal-title" className="text-lg sm:text-xl font-semibold text-alsero-dark-gray pr-2">
                                    {heritageModalRow.name}
                                </h2>
                                <button
                                    type="button"
                                    onClick={() => setHeritageModalRow(null)}
                                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors shrink-0"
                                    aria-label={t('tour_detail.close', 'Close')}
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-5 sm:p-6 overflow-y-auto overscroll-contain space-y-6">
                                {heritageModalGalleryUrls.length > 0 ? (
                                    <Swiper
                                        modules={[Navigation, Pagination]}
                                        navigation
                                        pagination={{ clickable: true }}
                                        className="heritage-site-modal-swiper rounded-xl border border-gray-100 bg-gray-50 overflow-hidden"
                                        spaceBetween={8}
                                        slidesPerView={1}
                                    >
                                        {heritageModalGalleryUrls.map((srcUrl, slideIdx) => (
                                            <SwiperSlide key={`${srcUrl}-${slideIdx}`}>
                                                <div className="relative aspect-[16/10] max-h-[min(52vh,420px)] w-full bg-gray-100">
                                                    <MediaImage
                                                        src={srcUrl}
                                                        alt={heritageModalRow.name}
                                                        className="absolute inset-0 h-full w-full object-contain"
                                                        width={1200}
                                                        height={750}
                                                    />
                                                </div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                ) : null}
                                {heritageDescriptionIsOpenable(heritageModalRow.description) ? (
                                    <section>
                                        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                                            {t('tour_detail.heritage_sites_modal_description', 'Description')}
                                        </h3>
                                        <div
                                            className="prose prose-sm sm:prose-base max-w-none mt-2 text-alsero-text-tour [&_a]:text-alsero-first"
                                            dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(heritageModalRow.description || '') }}
                                        />
                                    </section>
                                ) : null}
                                {(() => {
                                    const loc = heritageModalRow.location?.trim() ?? "";
                                    const lat = heritageModalRow.latitude;
                                    const lng = heritageModalRow.longitude;
                                    const hasCoords =
                                        lat != null &&
                                        lng != null &&
                                        Number.isFinite(Number(lat)) &&
                                        Number.isFinite(Number(lng)) &&
                                        Math.abs(Number(lat)) <= 90 &&
                                        Math.abs(Number(lng)) <= 180;
                                    if (!hasCoords && !loc) return null;
                                    const mapTitle = `${heritageModalRow.name} — ${t("tour_detail.heritage_sites_modal_map", "Location map")}`;
                                    return (
                                        <section>
                                            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                                                {t("tour_detail.heritage_sites_modal_location", "Location")}
                                            </h3>
                                            <div className="mt-2">
                                                <HeritageSiteEmbedMap
                                                    visible
                                                    latitude={lat ?? null}
                                                    longitude={lng ?? null}
                                                    addressQuery={loc || null}
                                                    mapLocale={language === "tr" ? "tr" : "en"}
                                                    title={mapTitle}
                                                />
                                            </div>
                                            {loc ? (
                                                <span className="sr-only whitespace-pre-line">{loc}</span>
                                            ) : null}
                                        </section>
                                    );
                                })()}
                                {heritageModalRow.working_hours?.trim() ? (
                                    <section>
                                        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                                            {t('tour_detail.heritage_sites_modal_hours', 'Opening hours')}
                                        </h3>
                                        <p className="mt-2 text-sm text-alsero-text-tour whitespace-pre-line">{heritageModalWorkingHoursDisplay}</p>
                                    </section>
                                ) : null}
                                {heritageModalTicketsPrepared.length > 0 ? (
                                    <section>
                                        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                                            {t('tour_detail.heritage_sites_modal_tickets', 'Tickets')}
                                        </h3>
                                        <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-alsero-dark-gray">
                                            {heritageModalTicketsPrepared.map((line, lineIdx) => (
                                                <li key={`${lineIdx}-${line.slice(0, 40)}`}>{line}</li>
                                            ))}
                                        </ul>
                                    </section>
                                ) : null}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <motion.div 
                className="grid grid-cols-12 mt-6 gap-6 md:mt-8 md:gap-8" 
                initial="hidden" 
                animate="visible" 
                variants={pageVariants}
            >
                <div className={`col-span-12 min-w-0 ${contentLayoutClass}`}>
                    <div className="sticky top-0 lg:top-5 z-[60] flex flex-row items-center gap-3 mb-4 bg-white/95 py-2 backdrop-blur-sm sm:bg-transparent sm:py-0 sm:backdrop-blur-none">
                        <div className="flex-1 min-w-0">
                            <ProductDetailMenu
                                links={displayMenuItems}
                                onOpenReservationModal={hasBookablePrivateOrDailyPrice ? () => setReservationModalOpen(true) : undefined}
                            />
                        </div>
                        <ScrollProgressIndicator sections={scrollSections} />
                    </div>
                    <ProductTourHero
                        productName={product?.name || t('tour_detail', 'Tour Detail')}
                        breadcrumbs={breadcrumbs}
                        rating={averageRating}
                        reviewCount={reviewCount}
                        tourDuration={tourData.tourDuration}
                        groupSize={{
                            min: tourData.minGroupSize,
                            max: tourData.maxGroupSize,
                        }}
                        language={language}
                        productId={product?.metas?.product_id != null ? String(product.metas.product_id) : (product?.id != null ? String(product.id) : null)}
                    />
                    {hasFeaturesCardData && (
                        <motion.section 
                            id="features"
                            className="mb-10 sm:mb-[75px] scroll-mt-20 min-w-0"
                            variants={slideFromBottomVariants}
                            aria-labelledby="features-heading"
                        >
                            <motion.h2 
                                id="features-heading"
                                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                                variants={headerVariants}
                            >
                                {t('features', 'Features')}
                            </motion.h2>
                            <ProductTourInfoCard
                                tourDuration={tourData.tourDuration}
                                season={tourData.season}
                                transfer={tourData.transfer}
                                tourType={tourData.tourType}
                                durationHours={tourData.durationHours}
                                startLocation={tourData.startLocation}
                                endLocation={tourData.endLocation}
                                intermediateStops={tourData.intermediateStops}
                                distance={tourData.distance}
                                minGroupSize={tourData.minGroupSize}
                                maxGroupSize={tourData.maxGroupSize}
                                groupSize={tourData.groupSize}
                                minAges={tourData.minAges}
                                guideLanguage={tourData.guideLanguage}
                                language={language}
                                fields={product?.fields || {}}
                                productId={product?.metas?.product_id ? String(product.metas.product_id) : null}
                            />
                        </motion.section>
                    )}
                    <motion.section 
                        id="information"
                        className="mb-[50px] scroll-mt-20"
                        variants={slideFromBottomVariants}
                        aria-labelledby="information-heading"
                    >
                        {(product?.description || tourData.relevantForYou || tourData.whatIsExpected) && !tourData.information.content && (
                            <motion.div variants={cardVariants} className="mb-8">
                                <ProductTourDescription
                                    description={product?.description || null}
                                    shortDescription={null}
                                    relevantForYou={tourData.relevantForYou}
                                    whatIsExpected={tourData.whatIsExpected}
                                    language={language}
                                    translations={translations}
                                />
                            </motion.div>
                        )}
                    </motion.section>
                    {showSpecialGuestsSection && (
                        <motion.section
                            id="special-guests"
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                            aria-labelledby="special-guests-heading"
                        >
                            <motion.h2
                                id="special-guests-heading"
                                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                                variants={headerVariants}
                            >
                                {specialGuestsMenuTitle}
                            </motion.h2>
                            <motion.div
                                variants={cardVariants}
                                className={`flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 lg:grid lg:gap-5 lg:overflow-visible lg:snap-none lg:pb-0 ${specialGuestsGridClass}`}
                            >
                                {specialGuests.map((guest, idx) => {
                                    const titleTrim = guest.title?.trim() ?? "";
                                    const nameTrim = guest.name.trim();
                                    const nameAlreadyHasTitle =
                                        titleTrim.length > 0 &&
                                        nameTrim.toLowerCase().includes(titleTrim.toLowerCase());
                                    const displayName = nameAlreadyHasTitle
                                        ? nameTrim
                                        : [nameTrim, titleTrim].filter(Boolean).join(" ");
                                    const titleAsRole =
                                        titleTrim.length > 0 &&
                                        !nameAlreadyHasTitle &&
                                        (titleTrim.includes(" ") || titleTrim.length > 28);

                                    return (
                                    <article
                                        key={`${guest.name}-${idx}`}
                                        className="snap-start flex shrink-0 w-[min(78vw,320px)] flex-col overflow-hidden sm:w-[min(48vw,320px)] lg:w-auto lg:max-w-none min-w-0 rounded-xl border border-gray-200 bg-white"
                                    >
                                        {guest.photo ? (
                                            <div className="relative aspect-[4/5] w-full overflow-hidden bg-neutral-100">
                                                <MediaImage
                                                    src={guest.photo}
                                                    alt={displayName}
                                                    fill
                                                    sizes="(max-width: 1024px) 78vw, 320px"
                                                    className="object-cover object-[center_22%]"
                                                    priority={idx === 0}
                                                />
                                            </div>
                                        ) : (
                                            <div className="flex aspect-[4/5] w-full items-center justify-center bg-neutral-100 text-3xl font-semibold text-gray-400">
                                                {nameTrim.charAt(0)}
                                            </div>
                                        )}
                                        <div className="flex flex-1 flex-col p-4 pt-3">
                                            <h3 className="text-base font-semibold leading-snug text-alsero-dark-gray">
                                                {displayName}
                                            </h3>
                                            {titleAsRole ? (
                                                <p className="mt-0.5 text-sm font-medium text-alsero-first">{titleTrim}</p>
                                            ) : null}
                                            {guest.description ? (
                                                <div
                                                    className="mt-2 flex-1 text-sm leading-relaxed text-alsero-text-tour line-clamp-4 [&_p]:my-1"
                                                    dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(guest.description) }}
                                                />
                                            ) : null}
                                            {guest.socialLinks && guest.socialLinks.length > 0 ? (
                                                <ul className="mt-3 space-y-1 border-t border-gray-100 pt-3">
                                                    {guest.socialLinks.map((link, linkIdx) => (
                                                        <li key={`${link.url}-${linkIdx}`}>
                                                            <a
                                                                href={link.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-sm text-alsero-first hover:underline"
                                                            >
                                                                {link.label}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : null}
                                        </div>
                                    </article>
                                    );
                                })}
                            </motion.div>
                        </motion.section>
                    )}
                    {tourDaysWithImages.length > 0 && (
                        <motion.section 
                            id="itinerary"
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                            aria-labelledby="itinerary-heading"
                        >
                            <motion.div 
                                className="flex items-center justify-between mb-8"
                                variants={headerVariants}
                            >
                                <h2 id="itinerary-heading" className="text-2xl lg:text-4xl font-bold text-alsero-dark-gray">
                                    {t('itinerary', 'Itinerary')}
                                </h2>
                                <button type="button"
                                    onClick={handleExpandAll}
                                    className="text-sm text-alsero-dark-gray underline hover:text-alsero-first transition-colors"
                                    aria-label={allExpanded ? t('collapse_all', 'Collapse All') : t('expand_all', 'Expand All')}
                                    aria-expanded={allExpanded}
                                >
                                    {allExpanded ? t('collapse_all', 'Collapse All') : t('expand_all', 'Expand All')}
                                </button>
                            </motion.div>
                            
                            <motion.div variants={cardVariants} className={hasMapLocations ? 'lg:grid lg:grid-cols-1 lg:gap-8 lg:items-start' : 'mb-8'}>
                                <div className={hasMapLocations ? 'lg:col-span-2 min-w-0' : ''}>
                                    <div ref={itineraryFancyboxRef} className="relative">
                                        {/* Dikey zaman çizgisi yalnızca md+; mobilde metin alanı tam genişlik */}
                                        <div
                                            className="absolute left-6 top-0 bottom-0 hidden w-0.5 bg-gray-300 md:block"
                                            aria-hidden
                                        />

                                        <div className="space-y-0">
                                            {tourDaysWithImages.map((day: any) => {
                                                const isOpen = allExpanded || openTourDay === day.id;
                                                const isDayHighlighted = highlightedDayId === day.id;
                                                const hasMapForDay = dayFirstMapIndex.has(day.id);
                                                
                                                return (
                                                    <motion.div
                                                        key={day.id}
                                                        data-day-id={day.id}
                                                        className={`relative pb-8 last:pb-0 transition-shadow ${
                                                            isDayHighlighted ? 'rounded-xl ring-2 ring-alsero-first/50 bg-red-50/30 px-2 -mx-2' : ''
                                                        }`}
                                                        variants={cardVariants}
                                                    >
                                                        <div className="flex min-w-0 flex-col gap-2 md:gap-3">
                                                            {/* Rozet + başlık yan yana; çok satırlı başlıkta rozet dikey ortada — açıklama bu satırın altında tam genişlik */}
                                                            <div className="flex flex-row items-center gap-3 md:gap-4">
                                                                <motion.button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (!allExpanded) {
                                                                            handleTourDayToggle(day.id)
                                                                        }
                                                                    }}
                                                                    className="shrink-0 cursor-pointer rounded-lg bg-alsero-first px-2 py-1.5 text-center text-[11px] font-bold leading-tight text-white shadow-md sm:text-xs md:min-h-[3.25rem] md:min-w-[4.5rem] md:px-2.5 md:py-3 md:text-base md:leading-snug"
                                                                    aria-label={t('tour_detail.day', 'Day') + ' ' + day.dayNumber + ': ' + day.title}
                                                                    aria-expanded={isOpen}
                                                                    aria-controls={`day-content-${day.id}`}
                                                                >
                                                                    {t('tour_detail.day', 'Day')} {day.dayNumber}
                                                                </motion.button>
                                                                <motion.button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        if (!allExpanded) {
                                                                            handleTourDayToggle(day.id)
                                                                        }
                                                                    }}
                                                                    className="min-w-0 flex-1 cursor-pointer text-left"
                                                                    disabled={allExpanded}
                                                                    aria-label={t('tour_detail.day', 'Day') + ' ' + day.dayNumber + ': ' + day.title}
                                                                    aria-expanded={isOpen}
                                                                    aria-controls={`day-content-${day.id}`}
                                                                >
                                                                    <h3
                                                                        id={`day-title-${day.id}`}
                                                                        className="text-base font-bold leading-snug text-alsero-dark-gray transition-colors hover:text-alsero-first md:text-lg md:leading-relaxed"
                                                                    >
                                                                        {day.title}
                                                                    </h3>
                                                                </motion.button>
                                                                {hasMapForDay && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => focusMapOnDay(day.id)}
                                                                        className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-alsero-first/30 bg-white px-2 py-1 text-[11px] font-semibold text-alsero-first hover:bg-red-50 sm:text-xs"
                                                                        aria-label={t('map_show_on_map', 'Show on map')}
                                                                    >
                                                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                        </svg>
                                                                        <span className="hidden sm:inline">{t('map_show_on_map', 'Show on map')}</span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <AnimatePresence initial={false} mode="wait">
                                                                {isOpen && (
                                                                        <motion.div
                                                                            key={`day-content-${day.id}`}
                                                                            id={`day-content-${day.id}`}
                                                                            role="region"
                                                                            aria-labelledby={`day-title-${day.id}`}
                                                                            initial={{ 
                                                                                height: 0, 
                                                                                opacity: 0,
                                                                            }}
                                                                            animate={{ 
                                                                                height: "auto", 
                                                                                opacity: 1,
                                                                            }}
                                                                            exit={{ 
                                                                                height: 0, 
                                                                                opacity: 0,
                                                                            }}
                                                                            transition={{ 
                                                                                height: {
                                                                                    type: "spring" as const,
                                                                                    stiffness: 300,
                                                                                    damping: 30,
                                                                                    mass: 0.8
                                                                                },
                                                                                opacity: {
                                                                                    duration: 0.3,
                                                                                    ease: "easeInOut"
                                                                                }
                                                                            }}
                                                                            className="overflow-hidden"
                                                                        >
                                                                        <motion.div 
                                                                            className="pt-2 md:pl-[calc(4.5rem+1rem)]"
                                                                            initial={{ opacity: 0 }}
                                                                            animate={{ opacity: 1 }}
                                                                            exit={{ opacity: 0 }}
                                                                            transition={{ 
                                                                                duration: 0.2,
                                                                                delay: 0.1
                                                                            }}
                                                                        >
                                                                            {day.description && (
                                                                                <div 
                                                                                    className="tour-itinerary-day-html text-alsero-text-tour leading-relaxed mb-4 prose prose-lg max-w-none prose-headings:text-alsero-dark-gray prose-a:text-alsero-first"
                                                                                    dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(day.description) }}
                                                                                />
                                                                            )}
                                                                            {(() => {
                                                                                const mealLine = formatTourDayMealDisplay(
                                                                                    day.meal,
                                                                                    day.meals_custom
                                                                                );
                                                                                const distLine = formatTourDayDistanceDisplay(day.distance);
                                                                                const showMeal = Boolean(mealLine);
                                                                                const showDist = Boolean(distLine);
                                                                                if (!showMeal && !showDist) return null;
                                                                                return (
                                                                                    <div className="mb-4 flex flex-row flex-wrap gap-x-3 gap-y-2">
                                                                                        {showMeal ? (
                                                                                            <p className="text-alsero-text-tour leading-relaxed">
                                                                                                <span className="font-semibold text-alsero-dark-gray">
                                                                                                    {t("tour_detail.meal", "Meal")}
                                                                                                </span>{": "}
                                                                                                {mealLine}
                                                                                            </p>
                                                                                        ) : null}
                                                                                        {showMeal && showDist ? ( ' | ') : null}
                                                                                        {showDist ? (
                                                                                            <p className="text-alsero-text-tour leading-relaxed">
                                                                                                <span className="font-semibold text-alsero-dark-gray">
                                                                                                    {t("tour_detail.distance", "Distance")}
                                                                                                </span>{": "}
                                                                                                {distLine}
                                                                                            </p>
                                                                                        ) : null}
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                            
                                                                            {day.images && day.images.length > 0 && (
                                                                                <div
                                                                                    className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4"
                                                                                    role="list"
                                                                                    aria-label={t("tour_detail.day_images", "Day photos")}
                                                                                >
                                                                                    {day.images.map((img: string, idx: number) => {
                                                                                        const url =
                                                                                            typeof img === "string"
                                                                                                ? img
                                                                                                : (img as { url?: string })?.url || "";
                                                                                        if (!url.trim()) return null;
                                                                                        const fullRaw = day.imagesFull?.[idx] ?? url;
                                                                                        const href =
                                                                                            typeof fullRaw === "string"
                                                                                                ? transformMediaUrl(fullRaw) || fullRaw
                                                                                                : url;
                                                                                        const alt = `${day.title} — ${t("tour_detail.day", "Day")} ${idx + 1}`;
                                                                                        const fancyGroup = `itinerary-day-${day.id}`;
                                                                                        return (
                                                                                            <a
                                                                                                key={`${day.id}-dayimg-${idx}`}
                                                                                                href={href}
                                                                                                data-fancybox={fancyGroup}
                                                                                                data-caption={alt}
                                                                                                className="group relative block aspect-[4/3] overflow-hidden rounded-lg border border-gray-200/80 bg-gray-50 shadow-sm ring-0 transition hover:ring-2 hover:ring-alsero-first/35"
                                                                                                role="listitem"
                                                                                            >
                                                                                                <MediaImage
                                                                                                    src={url}
                                                                                                    alt={alt}
                                                                                                    fill
                                                                                                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 260px"
                                                                                                    loading="lazy"
                                                                                                    className="object-cover transition duration-300 group-hover:scale-[1.02]"
                                                                                                />
                                                                                            </a>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            )}
                                                                        </motion.div>
                                                                    </motion.div>
                                                                )}
                                                            </AnimatePresence>
                                                        </div>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {hasMapLocations && (
                                    <div
                                        ref={mapSectionRef}
                                        className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start mt-8 lg:mt-0"
                                    >
                                    <motion.div 
                                        className={`relative overflow-hidden rounded-lg w-full transition-all duration-500 ${
                                            isStoryModeActive ? 'h-[28rem] md:h-[32rem] lg:h-[42rem]' : 'h-[28rem] lg:h-[42rem]'
                                        }`}
                                        variants={cardVariants}
                                    >
                                        <InteractiveMapStory
                                            mapRef={mapRef}
                                            points={mapStoryPoints}
                                            activeIndex={isStoryModeActive ? (mapActiveIndex ?? 0) : undefined}
                                            onActiveIndexChange={isStoryModeActive ? handleStoryIndexChange : undefined}
                                            onStoryModeChange={handleStoryModeChange}
                                            onOpenBrochure={product?.id != null ? () => setBrochureModalOpen(true) : undefined}
                                            onOpenRequestForm={() => setRequestFormModalOpen(true)}
                                        >
                                            <TourRouteMap
                                                ref={mapRef}
                                                className="w-full h-full rounded-lg"
                                                isSidebarOpen={isStoryModeActive}
                                                locations={mapLocationsForMap}
                                                activeMarkerIndex={mapActiveIndex}
                                                onMarkerClick={handleMapMarkerClick}
                                                dayLabelTemplate={t('map_day_label', 'Day {day}')}
                                                dayStopLabelTemplate={t('map_day_stop_label', 'Stop {current}/{total}')}
                                            />
                                        </InteractiveMapStory>
                                    </motion.div>
                                    <TourRouteMapLegend
                                        className="mt-3"
                                        startLabel={t('map_legend_start', 'Start')}
                                        stopLabel={t('map_legend_stop', 'Stop')}
                                        endLabel={t('map_legend_end', 'End')}
                                    />
                                    </div>
                                )}
                            </motion.div>
                            {tourDaysWithImages.length > 0 && !hasMapLocations && (
                                <p className="text-sm text-alsero-light-gray mt-4 mb-8" role="status">
                                    {t('tour_detail.map_requires_location', 'Harita, tur günlerine konum (enlem/boylam veya adres) eklendiğinde görüntülenecektir.')}
                                </p>
                            )}
                        </motion.section>
                    )}
                    
                    {((effectiveHasPrice ?? product?.has_price) ||
                        pricingPending ||
                        pricingLoadFailed ||
                        pricingPeriodsGroupForPriceSection.length > 0 ||
                        pricingPeriodsDailyForPriceSection.length > 0 ||
                        pricingPeriodsForPriceSection.length > 0) && (
                        <motion.section 
                            id="pricing"
                            className="mb-[75px] scroll-mt-20 min-w-0 max-w-full"
                            variants={slideFromBottomVariants}
                        >
                            <motion.h2 
                                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                                variants={headerVariants}
                            >
                                {t('price', 'Price')}
                            </motion.h2>
                            {pricingPending && (
                                <div className="animate-pulse space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-6" role="status" aria-live="polite">
                                    <div className="h-4 w-1/3 rounded bg-gray-200" />
                                    <div className="h-10 w-full rounded bg-gray-200" />
                                    <div className="h-10 w-full rounded bg-gray-200" />
                                </div>
                            )}
                            {!pricingPending && pricingLoadFailed && (
                                <p className="text-sm text-amber-800 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" role="alert">
                                    {t('tour_detail.pricing_load_error', 'Prices could not be loaded. Please refresh the page.')}
                                </p>
                            )}
                            {!pricingPending && pricingPeriodsGroupForPriceSection.length > 0 && (
                                <motion.div className="mb-8 min-w-0 max-w-full" variants={cardVariants}>
                                    <h3 className="text-lg font-semibold text-alsero-dark-gray mb-4">{t('tour_detail.group_tour_prices', 'Group tour prices')}</h3>
                                    <PriceDetailTable
                                        periods={pricingPeriodsGroupForPriceSection.map(period => {
                                            const mappedRows = period.rows
                                                .map((row) => ({
                                                    paxRange: row.paxRange,
                                                    perPersonDouble: row.perPersonDouble != null ? Number(row.perPersonDouble) : null,
                                                    singleRoom: row.singleRoom != null ? Number(row.singleRoom) : null,
                                                    extraBed: row.extraBed != null ? Number(row.extraBed) : null,
                                                    childrenFee: row.childrenFee != null ? Number(row.childrenFee) : null,
                                                    ...('childRules' in row && Array.isArray(row.childRules) ? { childRules: row.childRules } : {}),
                                                }))
                                                .filter((row) => {
                                                    const ppd = row.perPersonDouble;
                                                    return ppd != null && !Number.isNaN(ppd) && ppd > 0;
                                                });
                                            const fallbackPrice = groupMinPricePerPerson;
                                            const rows =
                                                mappedRows.length > 0
                                                    ? mappedRows
                                                    : fallbackPrice != null
                                                      ? [
                                                            {
                                                                paxRange: t('tour_detail.per_person_double_room', 'Per person (double room)'),
                                                                perPersonDouble: fallbackPrice,
                                                                singleRoom: null,
                                                                extraBed: null,
                                                                childrenFee: null,
                                                            },
                                                        ]
                                                      : [];
                                            return {
                                                packageName: period.packageName,
                                                periodStart: period.periodStart,
                                                periodEnd: period.periodEnd,
                                                rows,
                                            };
                                        }).filter((period) => period.rows.length > 0)}
                                        currency={product?.currency?.code || 'EUR'}
                                        variant="group"
                                        t={t}
                                    />
                                </motion.div>
                            )}
                            {pricingPeriodsDailyForPriceSection.length > 0 && (
                                <motion.div className="mb-8 min-w-0 max-w-full" variants={cardVariants}>
                                    <h3 className="text-lg font-semibold text-alsero-dark-gray mb-4">{t('tour_detail.daily_tour_prices', 'Daily tour prices')}</h3>
                                    <PriceDetailTable
                                        periods={pricingPeriodsDailyForPriceSection.map((period) => ({
                                            packageName: period.packageName,
                                            periodStart: period.periodStart,
                                            periodEnd: period.periodEnd,
                                            rows: period.rows.map((row) => ({
                                                paxRange: row.paxRange,
                                                perPersonDouble: row.perPersonDouble ?? null,
                                                singleRoom: row.singleRoom ?? null,
                                                extraBed: row.extraBed ?? null,
                                                childrenFee: row.childrenFee ?? null,
                                                ...('childRules' in row && Array.isArray(row.childRules) ? { childRules: row.childRules } : {}),
                                            })),
                                        }))}
                                        currency={product?.currency?.code || 'EUR'}
                                        variant="daily"
                                        t={t}
                                    />
                                </motion.div>
                            )}
                            {pricingPeriodsForPriceSection.length > 0 && (
                                <motion.div 
                                    className="min-w-0 max-w-full"
                                    variants={cardVariants}
                                >
                                    <h3 className="text-lg font-semibold text-alsero-dark-gray mb-4">{t('tour_detail.private_tour_prices', 'Private tour prices')}</h3>
                                    <PriceDetailTable
                                        periods={pricingPeriodsForPriceSection.map(period => ({
                                            packageName: period.packageName,
                                            periodStart: period.periodStart,
                                            periodEnd: period.periodEnd,
                                            rows: period.rows.map(row => ({
                                                paxRange: row.paxRange,
                                                perPersonDouble: row.perPersonDouble ?? null,
                                                singleRoom: row.singleRoom ?? null,
                                                extraBed: row.extraBed ?? null,
                                                childrenFee: row.childrenFee ?? null,
                                                ...('childRules' in row && Array.isArray(row.childRules) ? { childRules: row.childRules } : {}),
                                            })),
                                        }))}
                                        currency={product?.currency?.code || 'EUR'}
                                        variant="private"
                                        t={t}
                                    />
                                </motion.div>
                            )}
                        </motion.section>
                    )}
                    {!hasGroupPrice && tourData.departureDates.length > 0 && (
                        <motion.section
                            id="departure_dates"
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                            aria-labelledby="departure_dates-heading"
                        >
                            <motion.h2
                                id="departure_dates-heading"
                                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                                variants={headerVariants}
                            >
                                {t('departure_dates', 'Departure Dates')}
                            </motion.h2>
                            <motion.div variants={cardVariants}>
                                <DepartureDatesCalendar
                                    departureDates={tourData.departureDates}
                                    title=""
                                    language={language}
                                />
                            </motion.div>
                        </motion.section>
                    )}
                    {hasGroupPrice && groupDepartureDates.length > 0 && (
                        <motion.section
                            id="dates-and-availability"
                            className="mb-[75px] scroll-mt-20"
                            variants={mountSafeRevealVariants}
                            initial="hidden"
                            animate="visible"
                            aria-labelledby="dates-and-availability-heading"
                        >
                            <motion.h2
                                id="dates-and-availability-heading"
                                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-2"
                                variants={headerVariants}
                            >
                                {t('tour_detail.dates', 'Dates')}
                            </motion.h2>
                            <motion.p className="text-gray-600 mb-6" variants={headerVariants}>
                                {t('tour_detail.guaranteed_departures_and_prices', 'Garantili çıkış tarihleri ve fiyatları.')}
                                {groupMinPricePerPerson != null && (
                                    <span className="ml-1 font-medium text-alsero-dark-gray">
                                        {t('tour_detail.per_person_from', 'Kişi başı')} {currencySymbol}{Number(groupMinPricePerPerson).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                )}
                            </motion.p>
                            <motion.div variants={cardVariants} className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                                <div className="grid gap-3 md:grid-cols-12 md:items-end">
                                    <div className="md:col-span-5">
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                                            {t('tour_detail.month', 'Month')}
                                        </label>
                                        <TourDepartureMonthPicker
                                            monthOptions={monthOptions}
                                            priceByMonth={priceByMonth}
                                            selectedMonth={checkAvailabilityMonth}
                                            selectedMonthFallback={selectedMonthFallback}
                                            monthsWithDepartures={monthsWithDeparturesInPickerYear}
                                            currencySymbol={currencySymbol}
                                            language={language}
                                            year={monthPickerYear}
                                            onYearPrev={() => setMonthPickerYear((prev) => prev - 1)}
                                            onYearNext={() => setMonthPickerYear((prev) => prev + 1)}
                                            onMonthSelect={(value) => {
                                                setCheckAvailabilityMonth(value);
                                                setIsDatesSectionMonthPickerOpen(false);
                                            }}
                                            isOpen={isDatesSectionMonthPickerOpen}
                                            onTriggerClick={() => {
                                                setIsDatesSectionMonthPickerOpen((prev) => !prev);
                                                setIsMonthPickerOpen(false);
                                            }}
                                            triggerLabel={selectedMonthLabel}
                                            ariaLabelPrevYear={t('tour_detail.prev_year', 'Önceki yıl')}
                                            ariaLabelNextYear={t('tour_detail.next_year', 'Sonraki yıl')}
                                            ariaLabelMonth={t('tour_detail.month', 'Month')}
                                            triggerClassName="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-left text-sm text-alsero-dark-gray flex items-center justify-between hover:border-gray-400"
                                            containerRef={datesSectionMonthPickerRef}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                                            {t('tour_detail.adults', 'Adults')}
                                        </label>
                                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white">
                                            <StepperIconButton
                                                symbol="−"
                                                srLabel={t('tour_detail.decrease_adults', 'Decrease adults')}
                                                onClick={() => setCheckAvailabilityAdults((n) => Math.max(1, n - 1))}
                                                disabled={checkAvailabilityAdults <= 1}
                                                className="flex items-center justify-center w-10 h-10 text-gray-600 border-r border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                            />
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={String(checkAvailabilityAdults)}
                                                onChange={(e) => {
                                                    const digits = e.target.value.replace(/\D/g, '');
                                                    const v = digits ? parseInt(digits, 10) : 1;
                                                    const next = clampGuestSelection(
                                                        Number.isNaN(v) ? 1 : v,
                                                        checkAvailabilityChildren,
                                                    );
                                                    setCheckAvailabilityAdults(next.adults);
                                                    setCheckAvailabilityChildren(next.children);
                                                }}
                                                className="flex-1 min-w-0 w-12 h-10 py-0 text-center text-sm font-semibold text-alsero-dark-gray bg-transparent outline-none"
                                                aria-label={t('tour_detail.adults', 'Adults')}
                                            />
                                            <StepperIconButton
                                                symbol="+"
                                                srLabel={t('tour_detail.increase_adults', 'Increase adults')}
                                                onClick={() => {
                                                    const next = clampGuestSelection(
                                                        checkAvailabilityAdults + 1,
                                                        checkAvailabilityChildren,
                                                    );
                                                    setCheckAvailabilityAdults(next.adults);
                                                    setCheckAvailabilityChildren(next.children);
                                                }}
                                                disabled={atGuestQuotaCap}
                                                className="flex items-center justify-center w-10 h-10 text-gray-600 border-l border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                            />
                                        </div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                                            {t('tour_detail.children', 'Children')}
                                        </label>
                                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white">
                                            <StepperIconButton
                                                symbol="−"
                                                srLabel={t('tour_detail.decrease_children', 'Decrease children')}
                                                onClick={() => setCheckAvailabilityChildren((n) => Math.max(0, n - 1))}
                                                disabled={checkAvailabilityChildren <= 0}
                                                className="flex items-center justify-center w-10 h-10 text-gray-600 border-r border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                            />
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                value={String(checkAvailabilityChildren)}
                                                onChange={(e) => {
                                                    const digits = e.target.value.replace(/\D/g, '');
                                                    const v = digits ? parseInt(digits, 10) : 0;
                                                    const next = clampGuestSelection(
                                                        checkAvailabilityAdults,
                                                        Number.isNaN(v) ? 0 : v,
                                                    );
                                                    setCheckAvailabilityAdults(next.adults);
                                                    setCheckAvailabilityChildren(next.children);
                                                }}
                                                className="flex-1 min-w-0 w-12 h-10 py-0 text-center text-sm font-semibold text-alsero-dark-gray bg-transparent outline-none"
                                                aria-label={t('tour_detail.children', 'Children')}
                                            />
                                            <StepperIconButton
                                                symbol="+"
                                                srLabel={t('tour_detail.increase_children', 'Increase children')}
                                                onClick={() => {
                                                    const next = clampGuestSelection(
                                                        checkAvailabilityAdults,
                                                        checkAvailabilityChildren + 1,
                                                    );
                                                    setCheckAvailabilityAdults(next.adults);
                                                    setCheckAvailabilityChildren(next.children);
                                                }}
                                                disabled={atGuestQuotaCap}
                                                className="flex items-center justify-center w-10 h-10 text-gray-600 border-l border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                            />
                                        </div>
                                    </div>
                                    <div className="md:col-span-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsDatesSectionMonthPickerOpen(false);
                                                setIsMonthPickerOpen(false);
                                                scrollToPageSection('dates-and-availability');
                                            }}
                                            className="h-10 w-full rounded-lg bg-alsero-first px-4 text-sm font-semibold text-white hover:opacity-90"
                                        >
                                            {t('tour_detail.check_availability_btn', 'Check availability')}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                            <motion.div variants={cardVariants}>
                                {groupDepartureDatesLoading ? (
                                    <p className="p-6 text-gray-500">{t('loading', 'Loading')}...</p>
                                ) : filteredGroupDepartureDates.length === 0 ? (
                                    <p className="p-6 text-gray-500">{t('tour_detail.no_departures_month')}</p>
                                ) : (
                                    <>
                                    {guestsExceedQuota && (
                                        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
                                            {t(
                                                'tour_detail.quota_exceeded_guests',
                                                'The number of guests exceeds available seats for this month. Reduce adults or children to continue.',
                                            )}
                                        </p>
                                    )}
                                    <ul className="space-y-3">
                                        {filteredGroupDepartureDates.map((d) => {
                                            const priceRow = groupDeparturePrices.find((r) => r.departure_date === d.departure_date);
                                            const departureDiscountPct = priceRow?.discount_percentage ?? d.discount_percentage;
                                            const resolvedPrice = resolveGroupDeparturePrice(
                                                priceRow?.calculated_price,
                                                getBasePerPersonForDeparture(d),
                                                departureDiscountPct,
                                                totalGuestsSelected,
                                            );
                                            const discountedPerPersonPrice = resolvedPrice?.perPerson ?? null;
                                            const originalPerPersonPrice = resolvedPrice?.originalPerPerson ?? null;
                                            const hasDiscount = resolvedPrice?.hasDiscount ?? false;
                                            const discountPercentage = resolvedPrice?.discountPercentage ?? null;

                                            const availability = d.availability;
                                            const isSoldOut =
                                                availability === 'sold_out' ||
                                                (d.remaining_pax != null && d.remaining_pax <= 0);
                                            const isStopSaleStatus =
                                                d.status === 'stop_sale' || availability === 'stop_sale';
                                            const exceedsDepartureQuota =
                                                d.remaining_pax != null &&
                                                d.remaining_pax > 0 &&
                                                totalGuestsSelected > d.remaining_pax;
                                            const cannotBookDeparture =
                                                isSoldOut ||
                                                isStopSaleStatus ||
                                                exceedsDepartureQuota ||
                                                (d.is_bookable === false &&
                                                    (d.remaining_pax == null || d.remaining_pax <= 0));
                                            const isLimited =
                                                availability === 'limited' &&
                                                !exceedsDepartureQuota &&
                                                !isSoldOut;
                                            const itineraryDays = tourDaysWithImages.length > 0 ? tourDaysWithImages.length : null;
                                            const departureDate = new Date(d.departure_date);
                                            const computedNights =
                                                endDateDuration.nights ??
                                                (endDateDuration.days != null && endDateDuration.days > 1 ? endDateDuration.days - 1 : null) ??
                                                (itineraryDays != null && itineraryDays > 1 ? itineraryDays - 1 : null);
                                            const endDate = computedNights != null
                                                ? new Date(departureDate.getFullYear(), departureDate.getMonth(), departureDate.getDate() + computedNights)
                                                : null;
                                            const dateLabel = departureDate.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
                                            const weekdayLabel = departureDate.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-GB', { weekday: 'long' });
                                            const endDateLabel = endDate
                                                ? endDate.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                                                : null;
                                            const endWeekdayLabel = endDate
                                                ? endDate.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-GB', { weekday: 'long' })
                                                : null;
                                            return (
                                            <li key={d.id} className="rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-gray-300 hover:shadow-sm">
                                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                                    <div className="flex flex-col items-start">
                                                        <div className="flex flex-row items-start gap-4 mb-5">
                                                            {d.status === 'available' && !cannotBookDeparture && (
                                                                <span className="inline-flex items-center rounded-full bg-emerald-100 px-3.5 py-2 text-sm font-semibold text-emerald-800">
                                                                    {t('tour_detail.guaranteed_departure')}
                                                                </span>
                                                            )}
                                                            {isSoldOut && (
                                                                <span className="inline-flex items-center rounded-full bg-rose-100 px-3.5 py-2 text-xs font-semibold text-rose-800">
                                                                    {t('tour_detail.sold_out', 'Sold Out')}
                                                                </span>
                                                            )}
                                                            {isStopSaleStatus && !isSoldOut && (
                                                                <span className="inline-flex items-center rounded-full bg-rose-100 px-3.5 py-2 text-xs font-semibold text-rose-800">
                                                                    {t('tour_detail.status_stop_sale', 'Stop Sale')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-row items-center gap-2">
                                                            <div className="flex flex-col flex-wrap items-start">
                                                                <span className="text-lg font-semibold text-alsero-dark-gray">{dateLabel}</span>
                                                                <span className="text-sm text-gray-400 font-light capitalize">{weekdayLabel}</span>
                                                            </div>
                                                            <div className="inline-flex shrink-0 items-center justify-center px-10">
                                                                <svg className="fill-gray-400" width="16" height="15" viewBox="0 0 16 15" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M15.6686 6.9085L0.811792 0.0515204C0.575226 -0.0559055 0.292947 0.00809299 0.12838 0.211517C-0.0373304 0.41494 -0.0430445 0.704076 0.114666 0.913214L5.00026 7.42734L0.114666 13.9415C-0.0430445 14.1506 -0.0373304 14.4409 0.127237 14.6432C0.238091 14.7814 0.403802 14.8557 0.571797 14.8557C0.652938 14.8557 0.734079 14.8386 0.810649 14.8032L15.6674 7.94618C15.8709 7.85247 16 7.65019 16 7.42734C16 7.20449 15.8708 7.00221 15.6686 6.9085Z"/>
                                                                </svg>
                                                            </div>
                                                            {endDateLabel && (
                                                                <div className="flex flex-col h-full flex-wrap items-start">
                                                                    <span className="text-lg font-semibold text-alsero-dark-gray">{endDateLabel}</span>
                                                                    <span className="text-sm text-gray-400 font-light capitalize">{endWeekdayLabel}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="mb-2 flex flex-wrap items-center gap-2">
                                                            
                                                            {isLimited && !cannotBookDeparture && (
                                                                <span className="inline-flex items-center rounded-full bg-orange-100 px-3.5 py-2 text-xs font-semibold text-orange-800">
                                                                    {t('tour_detail.limited_availability', 'Limited availability')}
                                                                </span>
                                                            )}
                                                            {exceedsDepartureQuota && !isSoldOut && (
                                                                <p className="text-sm text-amber-800 mt-1">
                                                                    {t(
                                                                        'tour_detail.quota_exceeded_guests',
                                                                        'The number of guests exceeds available seats for this month. Reduce adults or children to continue.',
                                                                    )}
                                                                </p>
                                                            )}
                                                            {isSoldOut && (
                                                                <p className="text-sm text-gray-500 mt-1">
                                                                    {t('tour_detail.sold_out_hint', 'This departure is no longer available.')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col flex-wrap items-end gap-3 lg:justify-end">
                                                        {discountedPerPersonPrice != null && (
                                                            <div className="min-w-[180px] rounded-lg bg-gray-50 px-3 py-2">
                                                                {hasDiscount && discountPercentage != null && (
                                                                    <p className="mb-1 text-right text-xs font-semibold text-amber-700">
                                                                        {t('tour_detail.special_offer_with_discount', 'Special offer -:percent%').replace(':percent', String(Math.round(discountPercentage)))}
                                                                    </p>
                                                                )}
                                                                <div className="text-right text-base font-semibold text-alsero-dark-gray">
                                                                    {currencySymbol}{Number(discountedPerPersonPrice).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    <span className="ml-1 text-xs font-medium text-gray-500">/ {t('tour_detail.per_person', 'person')}</span>
                                                                </div>
                                                                {hasDiscount && originalPerPersonPrice != null && originalPerPersonPrice > discountedPerPersonPrice && (
                                                                    <p className="text-right text-xs text-gray-400 line-through">
                                                                        {currencySymbol}{Number(originalPerPersonPrice).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </p>
                                                                )}
                                                                <p className="text-right text-xs text-gray-500">
                                                                    {t('tour_detail.price_note_double_room', 'Price based on double room')}
                                                                </p>
                                                            </div>
                                                        )}
                                                        {cannotBookDeparture ? (
                                                            <span className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-500">
                                                                {t('tour_detail.unavailable', 'Unavailable')}
                                                            </span>
                                                        ) : (
                                                            <Link
                                                                href={bookNowHref}
                                                                prefetch={false}
                                                                onClick={() => {
                                                                    persistGroupSelectionToCheckoutCookie({
                                                                        departureDate: d.departure_date,
                                                                        periodId: d.period_id,
                                                                        perPersonPrice: discountedPerPersonPrice,
                                                                    });
                                                                }}
                                                                className="rounded-lg bg-alsero-first px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                                                            >
                                                                {t('tour_detail.pay_and_book_now', 'Pay & book now')}
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        );})}
                                    </ul>
                                    </>
                                )}
                            </motion.div>
                        </motion.section>
                    )}
                    {(tourData.includedServices.length > 0 || tourData.excludedServices.length > 0) && (
                        <motion.section 
                            id="services"
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                        >
                            <motion.h2
                                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                                variants={headerVariants}
                            >
                                {t('services', 'Services')}
                            </motion.h2>
                            <motion.div
                                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8"
                                variants={cardVariants}
                            >
                                <ProductTourServicesList
                                    included={tourData.includedServices}
                                    excluded={tourData.excludedServices}
                                    language={language}
                                    includedTitle={product?.fields?.included_services?.field_title}
                                    excludedTitle={product?.fields?.excluded_services?.field_title}
                                />
                            </motion.div>
                        </motion.section>
                    )}
                    {/* Oteller bölümü: hotelIds varsa bölüm render (lazy load: scroll veya sekme tıklanınca API isteği) */}
                    {hotelIds.length > 0 && (
                        <motion.section
                            id="price-services-hotel"
                            className="price-services-hotel mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                        >
                            <motion.h2
                                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                                variants={headerVariants}
                            >
                                {hotelsSectionLabel}
                            </motion.h2>
                            {(hotelEntries.length > 0 && orderedHotels.length > 0 && !hotelsLoading) ? (
                                (() => {
                                // Otel adı eşlemesi (alternatif otel adını çözmek için)
                                const hotelNameById: Record<number, string> = {};
                                const hotelById: Record<number, any> = {};
                                orderedHotels.forEach((h: any) => {
                                    if (typeof h?.id === 'number' && h?.name) {
                                        hotelNameById[h.id] = h.name;
                                        hotelById[h.id] = h;
                                    }
                                });
                                // Yalnızca tour_hotels satırlarından tablo üret (alternatif oteller ayrı satır olmaz).
                                const tableRows: HotelsTableRow[] = [];
                                hotelEntries.forEach((entry) => {
                                    const hotel = hotelById[entry.hotelId];
                                    if (!hotel) return;
                                    const alternativeNames = (entry.alternativeHotelIds ?? [])
                                        .map((id) => hotelNameById[id])
                                        .filter((n): n is string => Boolean(n));
                                    tableRows.push({
                                        id: hotel.id,
                                        rowKey: entry.rowKey,
                                        name: hotel.name,
                                        city: hotel.metas?.location || '',
                                        country: hotel.metas?.country || '',
                                        nights: entry.nights,
                                        stars: Number(hotel.metas?.stars) || 0,
                                        packageName: entry.package?.trim() || undefined,
                                        alternativeNames: alternativeNames.length > 0 ? alternativeNames : undefined,
                                    });
                                });
                                return tableRows.length > 0 ? (
                                    <HotelsTable rows={tableRows} t={t} />
                                ) : (
                                <motion.p className="text-alsero-dark-gray/70" variants={cardVariants}>
                                    {t('tour_detail_no_hotels', 'No hotel details available.')}
                                </motion.p>
                                );
                            })()
                            ) : hotelsLoading ? (
                                <motion.p className="text-alsero-dark-gray/70" variants={cardVariants}>
                                    {t('loading', 'Loading')}...
                                </motion.p>
                            ) : (
                                <motion.p className="text-alsero-dark-gray/70" variants={cardVariants}>
                                    {t('tour_detail_hotels_loading_hint', 'Scroll or click Hotels tab to load.')}
                                </motion.p>
                            )}
                            {tourHotelsNoteHtml ? (
                                <motion.div
                                    variants={cardVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className="mt-8 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/90 p-5 sm:p-6"
                                >
                                    <h3 className="mb-3 text-base font-semibold text-alsero-dark-gray sm:text-lg">
                                        {getVariable('tour_detail.hotels_note_title', 'Note')}
                                    </h3>
                                    <div
                                        className="tour-hotels-note max-w-none text-sm leading-relaxed text-alsero-dark-gray [&_a]:text-alsero-first [&_p]:mb-2 last:[&_p]:mb-0 [&_ul]:list-disc [&_ul]:pl-5"
                                        dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(tourHotelsNoteHtml) }}
                                    />
                                </motion.div>
                            ) : null}
                        </motion.section>
                    )}
                    {showHeritageSitesSection && (
                        <motion.section
                            id="heritage_sites"
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                            aria-labelledby="heritage-sites-heading"
                        >
                            <motion.h2
                                id="heritage-sites-heading"
                                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                                variants={headerVariants}
                            >
                                {t('tour_detail.heritage_sites', 'Heritage sites')}
                            </motion.h2>
                            <motion.ul
                                variants={cardVariants}
                                className="list-none space-y-3 p-0 m-0"
                                aria-label={t('tour_detail.heritage_sites', 'Heritage sites')}
                            >
                                {heritageSitesExpanded.map((row) => {
                                    const cover = row.cover_image_url?.trim() ?? '';
                                    const priceInfo = heritageTicketPriceCompact(row);
                                    const titleAttr =
                                        priceInfo.title && priceInfo.title !== priceInfo.display ? priceInfo.title : undefined;
                                    const canOpenModal = heritageSiteRowHasModalContent(row);
                                    const descExcerpt = heritageDescriptionCardExcerpt(row.description);
                                    const locPreview = row.location?.trim().replace(/\s+/g, " ") ?? "";
                                    const openModal = () => setHeritageModalRow(row);
                                    return (
                                        <motion.li
                                            key={row.id}
                                            variants={cardVariants}
                                            className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-alsero-text-tour"
                                        >
                                            {cover ? (
                                                canOpenModal ? (
                                                    <button
                                                        type="button"
                                                        onClick={openModal}
                                                        className="group shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 text-left shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-alsero-first/40"
                                                        aria-haspopup="dialog"
                                                        aria-expanded={heritageModalRow?.id === row.id}
                                                        aria-label={`${row.name} — ${t('tour_detail.heritage_sites_modal_open_details', 'Open details')}`}
                                                    >
                                                        <MediaImage
                                                            src={cover}
                                                            alt=""
                                                            className="h-28 w-28 object-cover transition-transform duration-200 group-hover:scale-[1.03] sm:h-32 sm:w-32"
                                                            width={128}
                                                            height={128}
                                                        />
                                                    </button>
                                                ) : (
                                                    <div className="shrink-0 overflow-hidden rounded-xl border border-gray-100 bg-gray-50 shadow-sm">
                                                        <MediaImage
                                                            src={cover}
                                                            alt={row.name}
                                                            className="h-28 w-28 object-cover sm:h-32 sm:w-32"
                                                            width={128}
                                                            height={128}
                                                        />
                                                    </div>
                                                )
                                            ) : null}
                                            <div className="min-w-0 flex-1">
                                                {canOpenModal ? (
                                                    <button
                                                        type="button"
                                                        className="text-left text-base font-semibold text-alsero-first leading-snug underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-alsero-first/40 rounded-sm"
                                                        onClick={openModal}
                                                        aria-haspopup="dialog"
                                                        aria-expanded={heritageModalRow?.id === row.id}
                                                        aria-label={`${row.name} — ${t('tour_detail.heritage_sites_modal_open_details', 'Open details')}`}
                                                    >
                                                        {row.name}
                                                    </button>
                                                ) : (
                                                    <h3 className="text-base font-semibold text-alsero-dark-gray leading-snug">{row.name}</h3>
                                                )}
                                                {descExcerpt ? (
                                                    <p className="mt-2 text-sm leading-relaxed text-gray-600 line-clamp-3">{descExcerpt}</p>
                                                ) : locPreview ? (
                                                    <p className="mt-2 text-sm leading-relaxed text-gray-600 line-clamp-2">{locPreview}</p>
                                                ) : null}
                                                <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                                                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                                        {priceInfo.display ? (
                                                            <span
                                                                className="text-base font-semibold text-alsero-dark-gray tabular-nums"
                                                                title={titleAttr}
                                                            >
                                                                {priceInfo.display}
                                                            </span>
                                                        ) : (
                                                            <span className="text-sm text-gray-400" aria-hidden>
                                                                —
                                                            </span>
                                                        )}
                                                    </div>
                                                    {canOpenModal ? (
                                                        <button
                                                            type="button"
                                                            onClick={openModal}
                                                            className="shrink-0 rounded-lg border border-alsero-first/30 bg-alsero-first/5 px-3 py-1.5 text-sm font-semibold text-alsero-first transition-colors hover:bg-alsero-first/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-alsero-first/40"
                                                            aria-haspopup="dialog"
                                                            aria-expanded={heritageModalRow?.id === row.id}
                                                        >
                                                            {t('tour_detail.heritage_sites_card_details', 'Details')}
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </motion.li>
                                    );
                                })}
                            </motion.ul>
                        </motion.section>
                    )}
                </div>
                <div className={`col-span-12 min-w-0 ${sidebarLayoutClass}`}>
                    {/* Mobilde sticky kapalı — form “ekran ortasında asılı” hissi vermesin; lg+ sağ sütun sabit */}
                    <div className="space-y-4 lg:sticky lg:top-5 lg:z-10">
                        {sidebarUseBookingTabs && (
                            <motion.div
                                className="rounded-xl rounded-b-none border border-gray-200 border-b-0 bg-white px-4 pt-4 pb-2 shadow-sm"
                                variants={mountSafeRevealVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                <h3 id="sidebar-booking-title" className="text-lg font-semibold text-alsero-dark-gray mb-3">
                                    {t('tour_detail.sidebar_booking_title', 'Book this tour')}
                                </h3>
                                <div
                                    role="tablist"
                                    aria-labelledby="sidebar-booking-title"
                                    className="flex gap-1 rounded-lg bg-gray-100 p-1"
                                >
                                    <button
                                        type="button"
                                        role="tab"
                                        id="sidebar-tab-btn-group"
                                        aria-selected={sidebarBookingTab === 'group'}
                                        aria-controls="sidebar-tab-panel-group"
                                        onClick={() => setSidebarBookingTab('group')}
                                        className={`flex-1 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${
                                            sidebarBookingTab === 'group'
                                                ? 'bg-white text-alsero-dark-gray shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        {t('tour_detail.sidebar_tab_group', 'Group tour')}
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        id="sidebar-tab-btn-private"
                                        aria-selected={sidebarBookingTab === 'private'}
                                        aria-controls="sidebar-tab-panel-private"
                                        onClick={() => setSidebarBookingTab('private')}
                                        className={`flex-1 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${
                                            sidebarBookingTab === 'private'
                                                ? 'bg-white text-alsero-dark-gray shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                    >
                                        {hasDailyTourPrice
                                            ? t('tour_detail.sidebar_tab_daily', 'Daily tour')
                                            : t('tour_detail.sidebar_tab_private', 'Private tour')}
                                    </button>
                                </div>
                            </motion.div>
                        )}
                        {/* Check availability — grup fiyatı ve çıkış tarihleri */}
                        {showSidebarGroupBooking && (
                            <motion.div
                                id="sidebar-tab-panel-group"
                                role={sidebarUseBookingTabs ? 'tabpanel' : undefined}
                                aria-labelledby={sidebarUseBookingTabs ? 'sidebar-tab-btn-group' : undefined}
                                hidden={sidebarUseBookingTabs ? sidebarBookingTab !== 'group' : undefined}
                                className={`border border-gray-200 bg-white p-4 shadow-sm ${
                                    sidebarUseBookingTabs
                                        ? 'rounded-xl rounded-t-none -mt-px shadow-sm'
                                        : 'rounded-xl'
                                } ${sidebarUseBookingTabs && sidebarBookingTab !== 'group' ? 'hidden' : ''}`}
                                variants={mountSafeRevealVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {!sidebarUseBookingTabs && (
                                    <h3 className="text-lg font-semibold text-alsero-dark-gray mb-3">
                                        {t('tour_detail.check_availability', 'Check Availability')}
                                    </h3>
                                )}
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('tour_detail.month', 'Month')}</label>
                                        <TourDepartureMonthPicker
                                            monthOptions={monthOptions}
                                            priceByMonth={priceByMonth}
                                            selectedMonth={checkAvailabilityMonth}
                                            selectedMonthFallback={selectedMonthFallback}
                                            monthsWithDepartures={monthsWithDeparturesInPickerYear}
                                            currencySymbol={currencySymbol}
                                            language={language}
                                            year={monthPickerYear}
                                            onYearPrev={() => setMonthPickerYear((prev) => prev - 1)}
                                            onYearNext={() => setMonthPickerYear((prev) => prev + 1)}
                                            onMonthSelect={(value) => {
                                                setCheckAvailabilityMonth(value);
                                                setIsMonthPickerOpen(false);
                                            }}
                                            isOpen={isMonthPickerOpen}
                                            onTriggerClick={() => {
                                                setIsMonthPickerOpen((prev) => !prev);
                                                setIsDatesSectionMonthPickerOpen(false);
                                            }}
                                            triggerLabel={selectedMonthLabel}
                                            ariaLabelPrevYear={t('tour_detail.prev_year', 'Önceki yıl')}
                                            ariaLabelNextYear={t('tour_detail.next_year', 'Sonraki yıl')}
                                            ariaLabelMonth={t('tour_detail.month', 'Month')}
                                            containerRef={monthPickerRef}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('tour_detail.adults', 'Adults')}</label>
                                            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white">
                                                <StepperIconButton
                                                    symbol="−"
                                                    srLabel={t('tour_detail.decrease_adults', 'Decrease adults')}
                                                    onClick={() => setCheckAvailabilityAdults((n) => Math.max(1, n - 1))}
                                                    disabled={checkAvailabilityAdults <= 1}
                                                    className="flex items-center justify-center w-10 h-10 text-gray-600 border-r border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                                />
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    value={String(checkAvailabilityAdults)}
                                                    onChange={(e) => {
                                                        const digits = e.target.value.replace(/\D/g, '');
                                                        const v = digits ? parseInt(digits, 10) : 1;
                                                        const next = clampGuestSelection(
                                                            Number.isNaN(v) ? 1 : v,
                                                            checkAvailabilityChildren,
                                                        );
                                                        setCheckAvailabilityAdults(next.adults);
                                                        setCheckAvailabilityChildren(next.children);
                                                    }}
                                                    className="flex-1 min-w-0 w-10 py-2 text-center text-sm font-semibold text-gray-900 bg-transparent outline-none"
                                                    aria-label={t('tour_detail.adults', 'Adults')}
                                                />
                                                <StepperIconButton
                                                    symbol="+"
                                                    srLabel={t('tour_detail.increase_adults', 'Increase adults')}
                                                    onClick={() => {
                                                        const next = clampGuestSelection(
                                                            checkAvailabilityAdults + 1,
                                                            checkAvailabilityChildren,
                                                        );
                                                        setCheckAvailabilityAdults(next.adults);
                                                        setCheckAvailabilityChildren(next.children);
                                                    }}
                                                    disabled={atGuestQuotaCap}
                                                    className="flex items-center justify-center w-10 h-10 text-gray-600 border-l border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('tour_detail.children', 'Children')}</label>
                                            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white">
                                                <StepperIconButton
                                                    symbol="−"
                                                    srLabel={t('tour_detail.decrease_children', 'Decrease children')}
                                                    onClick={() => setCheckAvailabilityChildren((n) => Math.max(0, n - 1))}
                                                    disabled={checkAvailabilityChildren <= 0}
                                                    className="flex items-center justify-center w-10 h-10 text-gray-600 border-r border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                                />
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    value={String(checkAvailabilityChildren)}
                                                    onChange={(e) => {
                                                        const digits = e.target.value.replace(/\D/g, '');
                                                        const v = digits ? parseInt(digits, 10) : 0;
                                                        const next = clampGuestSelection(
                                                            checkAvailabilityAdults,
                                                            Number.isNaN(v) ? 0 : v,
                                                        );
                                                        setCheckAvailabilityAdults(next.adults);
                                                        setCheckAvailabilityChildren(next.children);
                                                    }}
                                                    className="flex-1 min-w-0 w-10 py-2 text-center text-sm font-semibold text-gray-900 bg-transparent outline-none"
                                                    aria-label={t('tour_detail.children', 'Children')}
                                                />
                                                <StepperIconButton
                                                    symbol="+"
                                                    srLabel={t('tour_detail.increase_children', 'Increase children')}
                                                    onClick={() => {
                                                        const next = clampGuestSelection(
                                                            checkAvailabilityAdults,
                                                            checkAvailabilityChildren + 1,
                                                        );
                                                        setCheckAvailabilityAdults(next.adults);
                                                        setCheckAvailabilityChildren(next.children);
                                                    }}
                                                    disabled={atGuestQuotaCap}
                                                    className="flex items-center justify-center w-10 h-10 text-gray-600 border-l border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {guestsExceedQuota && (
                                        <p className="text-sm text-amber-800 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" role="alert">
                                            {t(
                                                'tour_detail.quota_exceeded_guests',
                                                'The number of guests exceeds available seats for this month. Reduce adults or children to continue.',
                                            )}
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsMonthPickerOpen(false);
                                            scrollToPageSection('dates-and-availability');
                                        }}
                                        className="w-full rounded-lg bg-alsero-first px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"
                                    >
                                        {t('tour_detail.check_availability_btn', 'Check availability')}
                                    </button>
                                    {/* Otomatik toplam fiyat ve indirim özeti (dönem/kişi/çocuk verisiyle hesaplanır) */}
                                    {groupDeparturePricesLoading && (
                                        <p className="text-sm text-gray-500 pt-2 border-t border-gray-100 mt-3">
                                            {sidebarPriceLabels.loading_prices}
                                        </p>
                                    )}
                                    {!groupDeparturePricesLoading && sidebarPriceSummary != null && (
                                        <div className="pt-3 mt-3 border-t border-gray-100 space-y-1.5">
                                            {sidebarPriceSummary.discountAmount > 0 && (
                                                <>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600">{sidebarPriceLabels.original_total}</span>
                                                        <span className="line-through text-gray-500">
                                                            {currencySymbol}{sidebarPriceSummary.originalTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600">{sidebarPriceLabels.discount}</span>
                                                        <span className="text-emerald-600 font-medium">
                                                            -{currencySymbol}{sidebarPriceSummary.discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            <div className="flex justify-between items-center text-sm font-semibold text-alsero-dark-gray pt-1">
                                                <span>{sidebarPriceLabels.total}</span>
                                                <span>
                                                    {currencySymbol}{sidebarPriceSummary.finalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500">
                                                {sidebarPriceLabels.price_based_on_guests}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                        {/* Günlük veya özel rezervasyon fiyatı */}
                        {showSidebarPrivateBooking && (
                            <motion.div
                                id={sidebarUseBookingTabs ? 'sidebar-tab-panel-private' : 'sidebar-reservation'}
                                role={sidebarUseBookingTabs ? 'tabpanel' : undefined}
                                aria-labelledby={sidebarUseBookingTabs ? 'sidebar-tab-btn-private' : undefined}
                                hidden={sidebarUseBookingTabs ? sidebarBookingTab !== 'private' : undefined}
                                className={`w-full max-w-full border border-gray-200 bg-white p-3 sm:p-4 shadow-sm ${
                                    sidebarUseBookingTabs
                                        ? 'rounded-xl rounded-t-none -mt-px shadow-sm'
                                        : 'rounded-xl'
                                } ${sidebarUseBookingTabs && sidebarBookingTab !== 'private' ? 'hidden' : ''}`}
                                variants={mountSafeRevealVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {!sidebarUseBookingTabs && (
                                    <h3 className="text-base sm:text-lg font-semibold text-alsero-dark-gray mb-3">
                                        {pricingPending
                                            ? t("tour_detail.loading_prices", "Loading prices…")
                                            : hasDailyTourPrice
                                            ? resolveSidebarFormHeading({ titleKind: 'reservation_daily', t })
                                            : privateSidebarHeading}
                                    </h3>
                                )}
                                {sidebarUseBookingTabs && pricingPending && (
                                    <p className="text-sm text-gray-500 mb-3">{t("tour_detail.loading_prices", "Loading prices…")}</p>
                                )}
                                {pricingPending ? (
                                    <ReservationBookingSidebarSkeleton />
                                ) : (
                                <ReservationBookingSidebar
                                    productId={product?.id as number}
                                    productType={product?.type || product?.product_type || 'tour'}
                                    productName={product?.name}
                                    hasPrice={effectiveHasPrice ?? (Array.isArray(tourPricingSource) && tourPricingSource.length > 0)}
                                    bookingFlow={((product as { booking_flow?: 'request_first' | 'instant_online' | 'bank_transfer_only' })?.booking_flow) ?? 'request_first'}
                                    bookingFlowConfigured={(product as { booking_flow_configured?: boolean })?.booking_flow_configured ?? false}
                                    language={language}
                                    languageId={languageId || undefined}
                                    agreements={reservationFormAgreements}
                                    agreementsLoading={reservationAgreementsLoading}
                                    requestFormTitle={requestFormTitleFromApi}
                                    translations={translations}
                                    minPax={minPaxForReservation}
                                    initialRequestFormDetails={initialRequestFormDetails}
                                    packages={(() => {
                                        const mode = reservationPricingPeriodType;
                                        const periods = (tourPricingSource as TourPricingPeriod[]).filter(
                                            (p) => (p.periodType ?? 'private') === mode,
                                        );
                                        const names = [...new Set(periods.map((p) => p.packageName ?? 'STANDARD'))];
                                        return names.map((packageName) => ({ packageName }));
                                    })()}
                                    onReservationSubmit={() => {}}
                                    showAsReservationForm={true}
                                    tourPricing={(tourPricingSource.length > 0 ? (tourPricingSource as TourPricingPeriod[]) : undefined)}
                                    currency={(product as { currency?: { code: string; symbol: string; symbol_position?: 'before' | 'after'; thousands_separator?: string; decimal_separator?: string } })?.currency}
                                    productMetas={(product as { metas?: { child_pax_age_threshold_private?: number; min_ages?: string | number | null } })?.metas}
                                    pricingPeriodType={reservationPricingPeriodType}
                                />
                                )}
                            </motion.div>
                        )}
                        {/* Rezervasyon fiyatı yoksa sağ barda gömülü talep formu */}
                        {showInlineRequestForm && product?.id != null && (
                            <motion.div className="w-full max-w-full rounded-xl border border-gray-200 bg-white p-3 sm:p-4 shadow-sm" variants={cardVariants}>
                                <h3 className="text-base sm:text-lg font-semibold text-alsero-dark-gray mb-3">{requestSidebarHeading}</h3>
                                <RequestForm
                                    initialFormDetails={initialRequestFormDetails ?? null}
                                    productId={product.id}
                                    productType={product?.type || product?.product_type || 'tour'}
                                    productName={product?.name}
                                    language={language}
                                    languageId={languageId ?? undefined}
                                    translations={translations}
                                />
                            </motion.div>
                        )}
                        {/* Sağ bar menü: Favoriler, Paylaş, Broşür, Tour Request Form (tüm rezervasyonlu tur tipleri) */}
                        <motion.div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm" variants={cardVariants}>
                            <div className="space-y-2">
                                <motion.button
                                    type="button"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={toggleFavorite}
                                    disabled={isFavoriteLoading}
                                    className={`w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-left ${
                                        isFavorite ? 'bg-alsero-first text-white' : 'bg-gray-100 text-alsero-dark-gray hover:bg-gray-200'
                                    } ${isFavoriteLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    aria-label={isFavorite ? t('tour_detail.remove_from_favorites', 'Favorilerden Çıkar') : t('tour_detail.add_to_favorites', 'Favorilere Ekle')}
                                >
                                    <svg className="w-4 h-4 shrink-0" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                    <span>{isFavorite ? t('tour_detail.remove_from_favorites', 'Favorilerden Çıkar') : t('tour_detail.add_to_favorites', 'Favorilere Ekle')}</span>
                                </motion.button>
                                <div className="w-full">
                                    <ProductShareButton
                                        url={typeof window !== 'undefined' ? window.location.href : ''}
                                        title={product?.name || 'Tour - Alsero Tours'}
                                        translationPrefix="tour_detail"
                                        showLabel
                                    />
                                </div>
                                {product?.id != null && (
                                    <motion.button
                                        type="button"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => setBrochureModalOpen(true)}
                                        className="w-full flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2.5 text-sm font-medium text-alsero-dark-gray hover:bg-gray-200 text-left"
                                        aria-label={t('tour_card_download_brochure', 'İndir Broşür')}
                                    >
                                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>{t('brochure_download_button_tooltip', 'Broşür indir')}</span>
                                    </motion.button>
                                )}
                                {showRequestFormSidebarButton && (
                                    <button
                                        type="button"
                                        onClick={() => setRequestFormModalOpen(true)}
                                        className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-alsero-first px-4 py-2.5 text-sm font-semibold text-alsero-first hover:bg-alsero-first/5 transition-colors"
                                    >
                                        {requestSidebarHeading}
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>
                <div className="col-span-12">
                    {/* tour_hotels: otel kartları + not yukarıda ayrı motion.section; tabFields tekrarı çift başlık/id üretiyordu.
                        other_descriptions (overview): aşağıda OverviewAccordion ile render edilir (ilk yüklemede tüm paneller açık). */}
                    {tourData.tabFields
                        .filter((tabGroup) => tabGroup.sectionId !== 'tour_hotels' && !isOtherDescriptionsGroup(tabGroup.groupKey))
                        .map((tabGroup) => (
                        <motion.section
                            key={tabGroup.sectionId}
                            id={tabGroup.sectionId}
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                        >
                            {tabGroup.showSectionTitle !== false && (
                                <motion.h2
                                    className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                                    variants={headerVariants}
                                >
                                    {resolveRepeaterSectionTitle(tabGroup.tabName, tabGroup.tabName, t)}
                                </motion.h2>
                            )}
                            {tabGroup.fields.map((field) => (
                                    <motion.div
                                        key={field.fieldKey}
                                        className="mb-8"
                                        variants={cardVariants}
                                    >
                                        {tabGroup.fields.length > 1 && (
                                            <h3 className="text-xl font-semibold text-alsero-dark-gray mb-4">
                                                {resolveRepeaterSectionTitle(field.fieldTitle, field.fieldTitle, t)}
                                            </h3>
                                        )}
                                        <div
                                            className="text-alsero-text-tour leading-relaxed prose prose-lg max-w-none"
                                            dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(field.content) }}
                                        />
                                    </motion.div>
                            ))}
                        </motion.section>
                    ))}

                    {/* Overview: "Diğer Açıklamalar" + tour_static; akordiyon (varsayılan hepsi açık) */}
                    {overviewAccordionItems.length > 0 && (
                        <motion.section
                            id="overview"
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                        >
                            <motion.h2
                                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                                variants={headerVariants}
                            >
                                {t('other_descriptions', 'Diğer Açıklamalar')}
                            </motion.h2>
                            <OverviewAccordion items={overviewAccordionItems} />
                        </motion.section>
                    )}

                    {resolvedProductFaqs.length > 0 && (
                        <motion.section 
                            id="faqs"
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                        >
                            <Faqs
                                language={language}
                                generalFaqs={resolvedProductFaqs}
                                title={t('faqs', 'FAQs')}
                                headingLevel="h2"
                            />
                        </motion.section>
                    )}
                    
                    <motion.section 
                        ref={reviewsTriggerRef}
                        id="reviews"
                        className="mb-[75px] scroll-mt-20"
                        variants={slideFromBottomVariants}
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            <div className="lg:col-span-8">
                                <ProductReviewsList
                                    reviews={reviews}
                                    averageRating={averageRating}
                                    reviewCount={reviewCount}
                                    isLoading={reviewsLoading}
                                    language={language}
                                    productId={product?.id || null}
                                    languageId={languageId || null}
                                    onLoadMore={async () => {
                                        if (!product?.id) return;
                                        const nextPage = currentPage + 1;
                                        try {
                                            const params = new URLSearchParams({
                                                reviewable_type: 'product',
                                                reviewable_id: String(product.id),
                                                status: 'approved',
                                                per_page: '15',
                                                page: String(nextPage),
                                            });
                                            if (languageId) {
                                                params.set('language_id', String(languageId));
                                            }
                                            const response = await fetch(`/api/customer-reviews?${params.toString()}`);
                                            if (response.ok) {
                                                const data = await response.json();
                                                setHasMoreReviews(data.meta?.has_more || false);
                                                const newReviews = Array.isArray(data.data) ? (data.data as Review[]) : [];
                                                setAppendedReviews((prev) => {
                                                    const map = new Map<number, Review>();
                                                    [...prev, ...newReviews].forEach((r) => map.set(r.id, r));
                                                    return Array.from(map.values());
                                                });
                                                setCurrentPage(nextPage);
                                            }
                                        } catch (error) {
                                            console.error('Load more reviews error:', error);
                                        }
                                    }}
                                    hasMore={hasMoreReviews}
                                    translations={translations}
                                />
                            </div>
                            <div className="lg:col-span-4">
                                <div className="sticky top-24">
                                    {effectiveReviewForm && (
                                        <ProductReviewForm
                                            productId={product?.id || null}
                                            productType="tour"
                                            language={language}
                                            languageId={languageId || null}
                                            form={effectiveReviewForm}
                                            translations={translations}
                                            pageUrls={pageUrls}
                                            contractsPageUrl={contractsPageUrl}
                                            defaultLanguage={defaultLanguage}
                                            initialFormDetails={initialFormDetails}
                                            onFormSubmit={async () => {
                                                try {
                                                    await refetchReviews();
                                                } catch {
                                                    /* Timeout/AbortError - unhandled rejection önlenir */
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.section>
                </div>
            </motion.div>
        </motion.div>
    );
}